import _ from 'lodash';
import React, { Component } from 'react';
import { Button, Collapse, FormCheck, Modal } from 'react-bootstrap';
import classNames from 'classnames';
import { findDOMNode } from 'react-dom';
import { isMobile } from 'react-device-detect';
import { DragSource, DropTarget } from 'react-dnd';
import { observable } from 'mobx';
import { observer } from 'mobx-react';

import toastr from '../../../toastr';
import ForcePartModal from '../modals/ForcePartModal';
import DefaultPartModal from '../modals/DefaultPartModal';
import NonStockingPartModal from '../modals/NonStockingPartModal';
import RemoveROLineItemModal from '../modals/RemoveROLineItemModal';
import SerialNumbersSelectionModal from '../modals/SerialNumbersSelectionModal';
import { scrollInvoiceBuilder } from './InvoiceBuilder';
import { IDetailSupplemental } from '../../typings/SupplementalDetail';
import { IAttachment, ILineItem } from '../../typings/LineItem';
import { IPartLineDetail } from '../../typings/PartLineDetail';
import { ICouponDetail } from '../../typings/CouponDetail';
import Label from '../Label';
import Model from '../../model/ShopKeeper.model';
import modal from '../../typings/Modal';
import config from '../../../../config';
import { ILoyaltyOffer } from '../../../loyalty/typings/LoyaltyOffer';

// Context menu support
import { Item, Menu, MenuProvider, Separator } from 'react-contexify';

// Drag and drop Firefox issue: https://github.com/react-dnd/react-dnd/issues/869

interface IProps {
  connectDragPreview?: Function;
  connectDragSource?: Function;
  connectDropTarget?: Function;
  isDragging?: boolean;
  itemIndex: number;
  fleetcrossModal: Function;
  lineItem: ILineItem;
  model: Model;
  moveLineItem: Function;
}

interface IContextMenuParams {
  id: string;
  lookupKey: string;
}

const contextMenuEvent = isMobile ? 'onClick' : 'onContextMenu';

const source = {
  beginDrag(props: IProps) {
    props.model.lineItemDragInProcess = true;
    return {
      index: props.itemIndex,
    };
  },
  endDrag(props: IProps) {
    props.model.lineItemDragInProcess = false;
  },
};

const target = {
  hover(props: IProps, monitor: any, component: any) {
    const dragIndex = monitor.getItem().index;
    const hoverIndex = props.itemIndex;

    // Don't replace line items with themselves
    if (dragIndex === hoverIndex) {
      return;
    }

    // Determine rectangle on screen
    const hoverBoundingRect = (findDOMNode(component) as Element).getBoundingClientRect();

    // Get vertical middle
    const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

    // Determine mouse position
    const clientOffset = monitor.getClientOffset();

    // Get pixels to the top
    const hoverClientY = clientOffset.y - hoverBoundingRect.top;

    // Only perform the move when the mouse has crossed half of the itDropTarget('LINEITEM', target, targetProps)(DragSource('LINEITEM', source, sourceProps)
    // When dragging downwards, only move when the cursor is below 50%DropTarget('LINEITEM', target, targetProps)(DragSource('LINEITEM', source, sourceProps)
    // When dragging upwards, only move when the cursor is above 50%

    // Dragging downwards
    if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
      return;
    }

    // Dragging upwards
    if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
      return;
    }

    if (!props.model.isLineFetching()) {
      // Actually move the line item (see InvoiceBuilder.tsx)
      props.moveLineItem(dragIndex, hoverIndex);

      // Mutating the monitor item here directly for the sake of performance.
      monitor.getItem().index = hoverIndex;
    }
  },
};

const getContainer = () => {
  return document.getElementById('shopkeeper');
};

@observer
class LineItem extends Component<IProps, {}> {
  model: Model;
  isDraggingEnabled: boolean;
  dragHandleClass: string;

  @observable descriptionEditMode = false;
  @observable commentEditMode = false;
  @observable quantityEditMode = false;
  @observable showConfirmRemoveModal = false;
  @observable showConfirmRemoveBoundCouponModal = false;
  @observable showForceManagerOverrideModal = false;
  @observable showForcePasswordModal = false;
  @observable showForcePartModal = false;
  @observable showDefaultPartModal = false;
  @observable showNonStockingPartModal = false;
  @observable showSerialNumbersSelectionModal = false;

  commentInput: HTMLInputElement;
  descriptionInput: HTMLInputElement;
  forcePasswordInput: HTMLInputElement;
  managerOverridePPCInput: HTMLInputElement;
  managerOverrideForcePasswordInput: HTMLInputElement;
  managerPersistForSessionCheckbox: HTMLInputElement;
  quantityInput: HTMLInputElement;

  // Tracks the bound coupon the user wishes to remove off of the line item (if applicable)
  activeCouponIndex = 0;
  activeCouponType: 'BIGQUERY' | 'BONDLOYALTY' = 'BIGQUERY';

  // SMM3 disables most line item features if the line isn't a sublet or default part
  isSMM3: boolean;

  isPIN4: boolean;

  // Used for PPC-managerPPC force reporting
  lastManagerOverridePPC: string | null = null;

  // Called after validating the force parts password
  // Post-validate actions could be different (update desc, update prices, etc.)
  forcePasswordValidatedCallback: Function;

  constructor(props: IProps) {
    super(props);
    this.model = props.model; // optional shortcut

    // These won't change in the lifetime of the line item whether this issued or not (RO mode)
    this.isDraggingEnabled = ['PIN1', 'PIN4', 'PSO3'].includes(this.model.runMode);
    this.dragHandleClass = this.isDraggingEnabled ? 'sk-dragHandle' : '';

    // Other line item flags such as "issuedToRO" must be referenced in render() for mobx change detection

    this.isSMM3 = props.model.runMode === 'SMM3';
    this.isPIN4 = props.model.runMode === 'PIN4';
  }

  // Convenience function which returns the description to be displayed
  activeDescription() {
    const lineItem = this.props.lineItem;

    // ts typeguard
    if (!lineItem.detail) {
      return '';
    }

    // Forced description always dominates
    if (lineItem.forcedDescription) {
      return lineItem.forcedDescription;

      // If PTB2 is set to show long desc on inquiry, do so (if a long desc is available)
    } else if (this.model.sysParams.printLongDescriptionOnInquiry && lineItem.detail.longDescription) {
      return lineItem.detail.longDescription;

      // Otherwise show the short desc.  If empty, check long desc as a fallback.  If both empty, result is ''.
    } else {
      return lineItem.detail.shortDescription ? lineItem.detail.shortDescription : lineItem.detail.longDescription;
    }
  }

  // Used to determine where to focus (have we popped a modal, etc.)
  componentDidUpdate() {
    // Are we in description edit mode?  If so, focus and select for fast update.
    if (this.descriptionEditMode) {
      this.descriptionInput.focus();
      this.descriptionInput.setSelectionRange(0, this.descriptionInput.value.length);
    }

    // Are we editing a line item comment / memo
    if (this.commentEditMode) {
      this.commentInput.focus();
      this.commentInput.setSelectionRange(0, this.commentInput.value.length);
    }

    // Are we in quantity edit mode? If so, focus and select for fast update.
    if (this.quantityEditMode) {
      this.quantityInput.focus();
      this.quantityInput.setSelectionRange(0, this.quantityInput.value.length);
    }

    if (this.props.lineItem.forcedEntry && this.props.lineItem.detail && !this.props.lineItem.initialForceModalRan) {
      this.props.lineItem.initialForceModalRan = true;
      this.priceOverride();
    }
  }

  copyToClipboard(text: string) {
    this.model.copyToClipboard(text, 'Part number copied to clipboard.');
  }

  /* Called on request to override a stocking part line item description.
     Checks force permissions and validates force PW first if need be.
  */
  async descriptionOverride({ managerOverride }: { managerOverride?: boolean } = {}) {
    // System wide setting (PTB2)
    if (!this.model.sysParams.partDescForce) {
      console.error('Description Force Disabled at System Level (PTB2)');
      return;
    }

    managerOverride = managerOverride || this.model.managerOverridePersistedForSession;

    // Per PPC permission basis
    if (this.model.forcePartsAllowed || managerOverride) {
      // password validated?
      if (this.model.ppcForcePasswordValidated || managerOverride) {
        // turn on description edit input
        this.descriptionEditMode = true;
      } else {
        // else, we must attempt to validate the override force password
        // and call this function back if successful in doing so
        this.forcePasswordValidatedCallback = this.descriptionOverride;
        this.showForcePasswordModal = true;
        setTimeout(() => {
          if (this.forcePasswordInput) {
            this.forcePasswordInput.focus();
          }
        }, 100);
      }

      // else this PPC does not have the ability to force parts
    } else {
      const attemptManagerOverride: boolean = await new Promise(resolve => {
        modal.confirm({
          title: 'Insufficient Permissions',
          message: 'Do you wish to perform a manager override?',
          confirmLabels: ['Yes', 'No'],
          callback: () => resolve(true),
          onClose: () => resolve(false),
        });
      });

      if (attemptManagerOverride) {
        this.forcePasswordValidatedCallback = this.descriptionOverride.bind(this, { managerOverride: true });
        this.showForceManagerOverrideModal = true;
        setTimeout(() => {
          if (this.managerOverridePPCInput) {
            this.managerOverridePPCInput.focus();
          }
        }, 100);
      }
    }
  }

  /* This wraps the model implementation of editLineItemPartNum with invoice builder scroll logic.
   */
  async editLineItemPartNum(newPartNum: string) {
    const lineItemCountPreEdit = this.model.lineItems.length;
    await this.model.editLineItemPartNum(this.props.itemIndex, newPartNum);
    // Is this the last item in the invoice builder and the edit didn't result in a combination elsewhere?
    if (
      this.model.lineItems.length === lineItemCountPreEdit &&
      this.model.lineItems.length === this.props.itemIndex + 1
    ) {
      // Scroll in this case to the new bottom of the edited line item which may be longer now.
      scrollInvoiceBuilder();
    }
  }

  /* hyperError enhances D3/QM error strings by detecting substitutions and
     supercedence recommendations and makes the associated partNums clickable
     and hot swappable in the InvoiceBuilder.
  */
  hyperError(error: string): JSX.Element | string {
    const { lineItem } = this.props;
    const errorParts = error.trim().split(' ');

    // Detect substitution errors - make partNums hotswappable in model on click
    // Per Ben, SOMETIMES supercedences are auto-corrected, other times they are not (fleet vs. non-fleet billed).
    if (errorParts.length > 1 && _.includes(['SUB'], errorParts[0])) {
      const action = errorParts.shift();
      return (
        <span>
          {action}{' '}
          {errorParts.map((subPart, subIndex) => {
            let title = subPart;

            // Does this DBPIP SUB error match a substition from DBPIPHELPER?
            // If so, use the description provided for the span title for UX.
            let dbpipHelperSub;

            // Supercedence case?
            if (lineItem.supercedenceDetailsSupplemental) {
              // Grovel through any constituent substituions seeking a match (very few parts to brute force through)
              for (const constituent of Object.keys(lineItem.supercedenceDetailsSupplemental)) {
                dbpipHelperSub = _.find(
                  lineItem.supercedenceDetailsSupplemental[constituent].substitutions,
                  o => o.partNum === subPart
                );
                if (dbpipHelperSub) {
                  break; // found
                }
              }

              // Standard stocking part case?
            } else if (lineItem.detailSupplemental) {
              dbpipHelperSub = _.find(lineItem.detailSupplemental.substitutions, o => o.partNum === subPart);
            }

            // If a substitution description was found, use it for the span title.
            if (dbpipHelperSub && dbpipHelperSub.description) {
              title = dbpipHelperSub.description;
            }

            const splitSupercedenceCase = lineItem.supercedenceDetails && lineItem.supercedenceDetails.length > 1;
            const nonSplitEscalatedQtyCase =
              lineItem.supercedenceDetails &&
              lineItem.supercedenceDetails.length === 1 &&
              lineItem.quantity !== lineItem.supercedenceDetails[0].quantity;

            return (
              <span key={subIndex}>
                {splitSupercedenceCase || nonSplitEscalatedQtyCase ? (
                  <span
                    title={title}
                    onClick={() => {
                      toastr.error(
                        'Error',
                        splitSupercedenceCase
                          ? 'Substitution hot-swap not available in split supercedence cases.'
                          : 'Substitution hot-swap not available in quantity escalated supercedence cases.'
                      );
                    }}
                  >
                    {subPart}
                  </span>
                ) : (
                  <span className="sk-hyperLink" title={title} onClick={() => this.editLineItemPartNum(subPart)}>
                    {subPart}
                  </span>
                )}
                &nbsp;
              </span>
            );
          })}
        </span>
      );
    }

    // Otherwise return error string unmodified
    return <span title={error}>{error}</span>;
  }

  /* Called on request to override pricing on a stocking part.
     Checks force permissions and validates force PW first if need be.
     Also checks per part force disallow and customer price codes.

     Supports manager override (added 6/2021).
  */
  async priceOverride({ managerOverride }: { managerOverride?: boolean } = {}) {
    // First check for per part price lock
    const perPartNoForcedPricing = _.get(this.props.lineItem, 'detailSupplemental.noForcedPricing', '') === 'Y';
    if (perPartNoForcedPricing) {
      toastr.error('Price Locked', 'This part has its pricing locked.');
      console.error(`${this.props.lineItem.partNum} has its price locked.`);
      return;
    }

    const customerPriceCodeDisallow = this.model.stdpForceSuppression;
    if (customerPriceCodeDisallow) {
      toastr.error('Force Not Allowed', 'This customer has price code STDP.');
      console.error('This customer has price code STDP which disallows forces.');
      return;
    }

    // Reminder this applies only to stocking parts.  Non-tracked and sublet lines will remain updatable.
    if (['PRO1', 'SRE2'].includes(this.model.runMode) && this.model.activeRONumber !== null) {
      const operationNumber = this.props.lineItem.roDetail!.operationNumber;
      const operationPriceCodeDisallow =
        this.model.activeRepairOrderDetails.operationDetails[operationNumber].stdpForceSuppression;

      if (operationPriceCodeDisallow) {
        toastr.error('Force Not Allowed', `Operation ${operationNumber} has a labor type with price code STDP.`);
        console.error(`Operation ${operationNumber} has a labor type with price code STDP which disallows forces.`);
        return;
      }
    }

    managerOverride = managerOverride || this.model.managerOverridePersistedForSession;

    if (this.model.forcePartsAllowed || managerOverride) {
      // password validated?
      if (this.model.ppcForcePasswordValidated || managerOverride) {
        // pop the price overrides modal for this line item
        this.showForcePartModal = true;
      } else {
        // else, we must attempt ot validate the override force password
        // and call this function back if successful in doing so
        // the callback exists because description override uses the same modal
        this.forcePasswordValidatedCallback = this.priceOverride;
        this.showForcePasswordModal = true;
        setTimeout(() => {
          if (this.forcePasswordInput) {
            this.forcePasswordInput.focus();
          }
        }, 100);
      }

      // else this PPC does not have the ability to force parts, suggest manager override
    } else {
      const attemptManagerOverride: boolean = await new Promise(resolve => {
        modal.confirm({
          title: 'Insufficient Permissions',
          message: 'Do you wish to perform a manager override?',
          confirmLabels: ['Yes', 'No'],
          callback: () => resolve(true),
          onClose: () => resolve(false),
        });
      });

      if (attemptManagerOverride) {
        this.forcePasswordValidatedCallback = this.priceOverride.bind(this, { managerOverride: true });
        this.showForceManagerOverrideModal = true;
        setTimeout(() => {
          if (this.managerOverridePPCInput) {
            this.managerOverridePPCInput.focus();
          }
        }, 100);
      }
    }
  }

  // Same as .toFixed but checks for NaN first and returns empty string if so
  safeToFixed(value: number, significantDigits: number): string {
    return Number.isNaN(value) ? '' : value.toFixed(significantDigits);
  }

  trimLegacyInquiryStrings(text: string) {
    return text.replace(/^>>>|<<<$/g, '').trim();
  }

  // Called to update a line item comment.
  updateComment() {
    const { lineItem } = this.props;

    const oldComment = lineItem.comment;
    const newComment = this.commentInput.value.trim();

    if (newComment !== '') {
      lineItem.comment = newComment;

      // If this is a PRO1 adjustment, adjust the line item on the RO.
      if (lineItem.issuedToRO && newComment !== oldComment) {
        this.model.adjustIssuedLineItem({ comment: newComment, type: 'COMMENT', itemIndex: this.props.itemIndex });
      }
    }

    this.commentEditMode = false;
  }

  // Called to actually update the forced description.
  updateDescription() {
    const lineItem = this.props.lineItem;
    const forcedDescription = this.descriptionInput.value.trim().toUpperCase();

    // 2nd term is a typescript typeguard that ensures detail exists
    if (forcedDescription === '') {
      delete lineItem.forcedDescription;
      this.descriptionEditMode = false;
      return;
    }

    if (forcedDescription !== this.activeDescription()) {
      lineItem.forcedDescription = forcedDescription.substring(0, 30);
    }

    this.descriptionEditMode = false;
  }

  // In the event quantity update fails, the model will auto-revert to the existing quantity.
  updateQuantity() {
    const { itemIndex, lineItem, model } = this.props;
    const confirmationSale = this.props.lineItem.confirmationSale;
    const currentQuantity = this.props.lineItem.quantity;
    const newQuantity = parseInt(this.quantityInput.value, 10);

    this.quantityEditMode = false;

    // PRO1/SMM3 only
    if (model.moveOperationsModeActive) {
      if (isNaN(newQuantity)) {
        // Revert back to currently issued quantity
        model.moveOperationsQueue.set(itemIndex, currentQuantity);
      } else if (newQuantity > 0 && newQuantity <= currentQuantity) {
        model.moveOperationsQueue.set(itemIndex, newQuantity);
      } else if (newQuantity > currentQuantity) {
        toastr.error('Error', 'Unable to move more than previously issued quantity.');
      }

      return;
    }

    if (!isNaN(newQuantity)) {
      if (confirmationSale && newQuantity > confirmationSale.confirmationQty) {
        toastr.error(
          'Error',
          'Unable to increase quantity sold on backorder part lines above original confirmation quantity.'
        );
        return;
      }

      if (newQuantity !== currentQuantity && newQuantity > 0) {
        this.model.editLineItemQuantity(itemIndex, newQuantity);

        if (confirmationSale && !confirmationSale.useOnHand && newQuantity < confirmationSale.confirmationQty) {
          modal.confirm({
            callback: () => {
              lineItem.confirmationSale!.remainderToStock = true;
            },
            message: 'Do you wish to REMOVE the remaining quantity from the confirmation and return it to stock?',
            title: 'Update Confirmation Quantity',
            confirmLabels: ['Yes', 'No'],
          });
        }

        if (confirmationSale && newQuantity === confirmationSale.confirmationQty) {
          delete lineItem.confirmationSale!.remainderToStock;
        }
      }
    }
  }

  async validateManagerOverride() {
    const ppc = this.managerOverridePPCInput.value;

    const validated = await this.model.validateManagerOverride({
      ppc,
      forcePartsPassword: this.managerOverrideForcePasswordInput.value,
      persist: this.managerPersistForSessionCheckbox.checked,
    });

    if (validated) {
      this.lastManagerOverridePPC = ppc;
      this.showForceManagerOverrideModal = false;
      this.forcePasswordValidatedCallback();
    }
  }

  validatePPCForcePassword() {
    // no-op
    if (this.forcePasswordInput.value === '') {
      return;
    }

    /* Ask the model to validate the password.
       If successful, model.ppcForcePasswordValidated is set to true in the model.
       Turn off the force password modal and call the force password validated callback.
    */
    if (this.model.validatePPCForcePassword(this.forcePasswordInput.value)) {
      this.showForcePasswordModal = false;
      this.forcePasswordValidatedCallback();
    }
  }

  /* RENDER METHODS */

  renderAttachments(partNum: string, attachments: IAttachment[]) {
    const imageAttachments = attachments.filter(
      o => o.mimeType.startsWith('image') || o.mimeType === 'application/pdf'
    );
    if (imageAttachments.length > 0) {
      return (
        <table className="sk-lineItemCols">
          <tbody>
            <tr className="sk-lineItemAttachments">
              <td />
              <td
                onClick={() =>
                  modal.open('attachments', {
                    itemId: partNum,
                    itemName: 'PIV1',
                    readOnly: true,
                  })
                }
              >
                {imageAttachments.map((o, i) => (
                  <div className="sk-thumbnail-wrapper" key={`thumb-${i}`}>
                    <img src={`${config.serverUrl}/api/attachment/${o.key}/thumbnail?resize=60px`} />
                  </div>
                ))}
              </td>
            </tr>
          </tbody>
        </table>
      );
    } else {
      return null;
    }
  }

  renderCollapsableAlternatives({
    detailSupplemental,
    suppressHotSwap = false,
    suppressHotSwapMsg = '',
  }: {
    detailSupplemental: IDetailSupplemental;
    suppressHotSwap?: boolean;
    suppressHotSwapMsg?: string;
  }) {
    if (this.props.lineItem.confirmationSale || this.props.lineItem.issuedToRO) {
      return null;
    }

    const { lineItem, model } = this.props;

    // The wrapper div is necessary for smooth animations inside Collapse
    return (
      <Collapse in={(model.expandAllAlternatives || lineItem.showPartAlternatives) && !model.hideAdditionalDetail}>
        <div>
          <div className="well">
            <table className="sk-lineItemCols" onContextMenu={evt => evt.preventDefault()}>
              <tbody>
                {detailSupplemental && detailSupplemental.alternateDivisions.length > 0 && (
                  <tr className="sk-lineItemAlternative">
                    <td />
                    <td>
                      <strong>Alt. Divs:</strong>
                      {detailSupplemental.alternateDivisions.map(
                        (alternateDivision: { partNum: string; onHand: string }, index: number) => {
                          let divisionTitle = '';

                          // parts should always be in the format division:partNum, but double check
                          if (alternateDivision.partNum[1] === ':') {
                            divisionTitle = model.partDivisions.get(alternateDivision.partNum[0]) || '';
                          }

                          return (
                            <div
                              key={index}
                              className={suppressHotSwap ? 'sk-alternate-item' : 'sk-alternate-item sk-hyperLink'}
                              title={divisionTitle}
                              onClick={() => {
                                if (suppressHotSwap) {
                                  toastr.error('Error', suppressHotSwapMsg);
                                } else {
                                  this.editLineItemPartNum(alternateDivision.partNum);
                                }
                              }}
                            >
                              {alternateDivision.partNum}({alternateDivision.onHand})
                            </div>
                          );
                        }
                      )}
                    </td>
                  </tr>
                )}

                {detailSupplemental && detailSupplemental.branchInventory.length > 0 && (
                  <tr className="sk-lineItemAlternative">
                    <td />
                    <td>
                      <strong>Alt. Branches:</strong>
                      {Object.keys(detailSupplemental.cachedBranchTotals).map((branch, index) => {
                        return (
                          <div
                            key={`sk-ba-${index}`}
                            className="sk-alternate-item sk-hyperLink"
                            title={model.branchTitleDict[branch] || ''}
                            onClick={() => {
                              model.activeBranchInventory = detailSupplemental.branchInventory;
                              model.showAlternateBranchesModal = true;
                            }}
                          >
                            {branch}({detailSupplemental.cachedBranchTotals[branch]})
                          </div>
                        );
                      })}
                    </td>
                  </tr>
                )}

                {detailSupplemental && detailSupplemental.substitutions.length > 0 && (
                  <tr className="sk-lineItemAlternative">
                    <td />
                    <td>
                      <strong>Part Subs:</strong>
                      {detailSupplemental.substitutions.map((substitution, index: number) => {
                        return (
                          <div
                            key={index}
                            className={suppressHotSwap ? 'sk-alternate-item' : 'sk-alternate-item sk-hyperLink'}
                            title={substitution.description}
                            onClick={() => {
                              if (suppressHotSwap) {
                                toastr.error('Error', suppressHotSwapMsg);
                              } else {
                                this.editLineItemPartNum(substitution.partNum);
                              }
                            }}
                          >
                            {substitution.partNum}({substitution.onHand})
                          </div>
                        );
                      })}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Collapse>
    );
  }

  // Memo/Comment lines are both draggable and in-place editable with a double click
  renderCommentCell(comment: string, titleOverride: string | null = null) {
    if (this.commentEditMode) {
      return (
        <td>
          <input
            defaultValue={comment}
            onBlur={() => this.updateComment()}
            onKeyPress={evt => {
              if (evt.key === 'Enter') {
                this.updateComment();
              }
            }}
            maxLength={90}
            ref={(ref: HTMLInputElement) => (this.commentInput = ref)}
            style={{ lineHeight: 1, width: '100%' }}
            type="text"
          />
        </td>
      );

      // Otherwise, not in edit mode, simply render comment to grid
    } else {
      return (
        <td
          className={this.dragHandleClass}
          onDoubleClick={() => {
            if (!this.isSMM3 && !this.model.moveOperationsModeActive) {
              this.commentEditMode = true;
            }
          }}
        >
          <span className={!this.isSMM3 ? 'sk-commentTextEditable' : ''} title={titleOverride || comment}>
            {comment}
          </span>
        </td>
      );
    }
  }

  renderCoreCharge(
    detail: IPartLineDetail,
    detailSupplemental: IDetailSupplemental,
    contextMenuParams: IContextMenuParams | null
  ) {
    const lineItem = this.props.lineItem;
    const coreListPrice = this.safeToFixed(detail.coreListPrice, 2);
    const coreTradePrice = this.safeToFixed(detail.coreTradePrice, 2);
    const dirtyCoreDesc = _.get(detailSupplemental, 'dirtyCoreDesc', `${detail.coreDescription} (DIRTY)`);

    // Based on FIFO net of the core unless PTB2 is set to use published net in GP calcs
    const gpCoreNetPrice = this.model.sysParams.publishedNetForGrossProfit
      ? detail.corePublishedNetPrice
      : detail.coreNetPrice;
    let grossProfitPercentage = !this.model.sysParams.displayGrossProfitOnInquiry
      ? ''
      : coreTradePrice === '0.00'
      ? '0'
      : Math.trunc(((detail.coreTradePrice - gpCoreNetPrice) / detail.coreTradePrice) * 100).toFixed(0);

    let coreNetPriceDisplay = this.model.sysParams.displayNetOnPartsInquiry
      ? this.safeToFixed(detail.corePublishedNetPrice, 2)
      : '';

    if (this.model.hideAdditionalDetail) {
      coreNetPriceDisplay = '';
      grossProfitPercentage = '';
    }

    // Core quantity prefix only used on PRO1/SMM3
    const coreQty =
      lineItem.issuedToRO &&
      lineItem.detail &&
      lineItem.detail.coreAttached &&
      lineItem.quantity !== lineItem.detail.coreQuantity
        ? `${lineItem.detail.coreQuantity} x `
        : '';

    return (
      <>
        <tr className="sk-coreCharge">
          <td />
          {contextMenuParams ? (
            <td>
              <MenuProvider event={contextMenuEvent} id={contextMenuParams.id}>
                &nbsp;
              </MenuProvider>
            </td>
          ) : (
            <td />
          )}
          <td />
          <td>
            <span title={detail.coreDescription}>
              {coreQty}
              {detail.coreDescription}
            </span>
          </td>
          <td />
          <td />
          <td />
          <td>
            <span title={coreNetPriceDisplay}>{coreNetPriceDisplay}</span>
          </td>
          <td>
            <span title={coreListPrice}>{coreListPrice}</span>
          </td>
          <td>
            <span title={coreTradePrice}>{coreTradePrice}</span>
          </td>
          <td>
            <span title={grossProfitPercentage}>{grossProfitPercentage}</span>
          </td>
          <td />
        </tr>
        {(this.props.model.suppressCoreCharges || this.props.lineItem.suppressCoreCharge) && (
          <tr className="sk-coreCharge">
            <td />
            {contextMenuParams ? (
              <td>
                <MenuProvider event={contextMenuEvent} id={contextMenuParams.id}>
                  &nbsp;
                </MenuProvider>
              </td>
            ) : (
              <td />
            )}
            <td />
            <td>
              <span title={dirtyCoreDesc}>{dirtyCoreDesc}</span>
            </td>
            <td />
            <td />
            <td />
            <td />
            <td />
            <td>
              <span title={`-${coreTradePrice}`}>-{coreTradePrice}</span>
            </td>
            <td />
            <td />
          </tr>
        )}
      </>
    );
  }

  renderCoupons(couponDetail: ICouponDetail[]) {
    return couponDetail.map((coupon, couponIndex) => {
      const couponNetPrice = this.safeToFixed(coupon.defaultPartNetPrice, 2);
      const couponListPrice = this.safeToFixed(coupon.defaultPartListPrice, 2);
      const couponTradePrice = this.safeToFixed(coupon.defaultPartTradePrice, 2);

      return (
        <React.Fragment key={couponIndex}>
          <tr className="sk-lineItemD3QMcomment">
            <td>
              <span title={`${coupon.quantity}`}>{coupon.quantity}</span>
            </td>
            <td>
              <span title={coupon.defaultPartNum}>{coupon.defaultPartNum}</span>
            </td>
            <td />
            <td>
              <span title={coupon.defaultPartDescription}>{coupon.defaultPartDescription}</span>
            </td>
            <td />
            <td>
              <span title={coupon.priceSymbol}>{coupon.priceSymbol}</span>
            </td>
            <td>
              <span title={coupon.stockClass}>{coupon.stockClass}</span>
            </td>
            <td>
              {this.model.sysParams.displayNetOnPartsInquiry && !this.model.hideAdditionalDetail && (
                <span title={couponNetPrice}>{couponNetPrice}</span>
              )}
            </td>
            <td>
              <span title={couponListPrice}>{couponListPrice}</span>
            </td>
            <td>
              <span title={couponTradePrice}>{couponTradePrice}</span>
            </td>
            <td />
            <td>
              <span
                className="sk-removeItemIcon"
                onClick={() => {
                  this.activeCouponType = 'BIGQUERY';
                  this.activeCouponIndex = couponIndex;
                  this.showConfirmRemoveBoundCouponModal = true;
                }}
                title="Remove Attached Coupon from Line Item"
              >
                <i className="zmdi zmdi-delete zmdi-hc-lg" />
              </span>
            </td>
          </tr>
          {!this.model.hideAdditionalDetail &&
            coupon.defaultPartSupplementalDetails &&
            coupon.defaultPartSupplementalDetails.length > 0 && (
              <tr className="sk-lineItemD3QMcomment">
                <td />
                <td />
                <td />
                <td colSpan={9}>
                  {coupon.defaultPartSupplementalDetails.map((detail, detailIndex) => {
                    detail = this.trimLegacyInquiryStrings(detail);
                    return (
                      <div key={detailIndex}>
                        <span title={detail}>{detail}</span>
                      </div>
                    );
                  })}
                </td>
              </tr>
            )}
        </React.Fragment>
      );
    });
  }

  renderD3QMComment(item: IPartLineDetail, contextMenuParams: IContextMenuParams | null) {
    if (this.props.lineItem.confirmationSale || this.props.lineItem.issuedToRO) {
      return null;
    }

    // D3/QM is prone to adding empty comments
    const commentsExist = !(item.comments.length === 1 && item.comments[0] === '');
    return (
      commentsExist &&
      item.comments.map((comment, index) => {
        // Note these are comments from D3/QM regarding the line item, NOT shopkeeper lineItem comments
        comment = this.trimLegacyInquiryStrings(comment);

        // We get this information from DBPIPHELPER in a more structured form
        if (_.startsWith(comment, 'PO -') || _.startsWith(comment, 'ON HOLD')) {
          return null;
        }

        return (
          <tr className="sk-lineItemD3QMcomment" key={index}>
            <td />
            {contextMenuParams ? (
              <td>
                <MenuProvider event={contextMenuEvent} id={contextMenuParams.id}>
                  &nbsp;
                </MenuProvider>
              </td>
            ) : (
              <td />
            )}
            <td />
            <td colSpan={9}>
              <span title={comment}>{comment}</span>
            </td>
          </tr>
        );
      })
    );
  }

  // Supports editing an existing default part line item
  renderDefaultPartDetailCell(value: string) {
    const editable = this.props.model.isLineItemCellEditable(this.props.itemIndex);

    return (
      <td
        onClick={() => {
          if (editable) {
            this.showDefaultPartModal = true;
          }
        }}
        className={editable ? 'sk-editField' : ''}
      >
        <span title={value}>{value}</span>
      </td>
    );
  }

  // Used by coupons (@COUPON/@DISCOUNT)
  renderDefaultPartSupplementalDetails(details: string[] | undefined) {
    return (
      details &&
      details.map((detail, index) => {
        // Note these are comments from D3/QM regarding the line item, NOT shopkeeper lineItem comments
        detail = this.trimLegacyInquiryStrings(detail);

        return (
          <tr className="sk-lineItemD3QMcomment" key={index}>
            <td />
            <td />
            <td />
            <td colSpan={9}>
              <span title={detail}>{detail}</span>
            </td>
          </tr>
        );
      })
    );
  }

  // This renderer is used only by stocking parts.
  // Non-tracked & default parts pop a modal. Supercedence trails are not overrideable.
  renderStockingPartDescriptionCell(description: string, descriptionLong: string = '') {
    if (this.descriptionEditMode) {
      return (
        <td>
          <input
            defaultValue={description}
            maxLength={30}
            onBlur={() => this.updateDescription()}
            onKeyPress={evt => {
              if (evt.key === 'Enter') {
                this.updateDescription();
              }
            }}
            ref={(ref: HTMLInputElement) => (this.descriptionInput = ref)}
            style={{ lineHeight: 1, width: '100%' }}
            type="text"
          />
        </td>
      );

      // Otherwise, not in edit mode, simply render description to grid
      // Stocking part description override capability is configured at a system wide level (PTB2),
      // as well as on a per PPC permission basis.
    } else {
      // const editable = !this.props.lineItem.issuedToRO;
      const editable = ['PIN1'].includes(this.props.model.runMode);

      return (
        <td
          onClick={() => {
            if (editable) {
              this.descriptionOverride();
            }
          }}
          className={classNames({
            'sk-editField': editable && this.model.sysParams.partDescForce && this.model.forcePartsAllowed,
          })}
        >
          <span title={descriptionLong || description}>{description}</span>
        </td>
      );
    }
  }

  // This could be referencing the "errors" on a standard part line, or the errors on
  // a supercedence constitutent of a part line. Thus, we don't reference this.props.lineItem.
  renderErrors(item: IPartLineDetail, contextMenuParams: IContextMenuParams | null) {
    if (this.props.lineItem.confirmationSale || this.props.lineItem.issuedToRO) {
      return null;
    }

    // D3/QM is prone to adding empty errors
    const errorsExist = !(item.errorText.length === 1 && item.errorText[0] === '');
    return (
      errorsExist &&
      item.errorText.map((error, index) => {
        error = this.trimLegacyInquiryStrings(error);

        // Always filter USE and SUPER hints which D3/QM provides via "error" strings, oddly
        if (/^(USE |SUPER )/.test(error)) {
          if (this.props.model.logSuperChains) {
            console.warn(`${item.partNum} - ${error}`);
          }
          return null;
        }

        return (
          <tr className="sk-lineItemD3QMerror" key={index}>
            <td />
            {contextMenuParams ? (
              <td>
                <MenuProvider event={contextMenuEvent} id={contextMenuParams.id}>
                  &nbsp;
                </MenuProvider>
              </td>
            ) : (
              <td />
            )}
            <td />
            <td colSpan={9}>{this.hyperError(error)}</td>
          </tr>
        );
      })
    );
  }

  renderFee(detail: IPartLineDetail, contextMenuParams: IContextMenuParams | null) {
    const feeListPrice = this.safeToFixed(detail.feeListPrice, 2);
    const feeTradePrice = this.safeToFixed(detail.feeTradePrice, 2);

    // Based on FIFO net of the core unless PTB2 is set to use published net in GP calcs
    const gpFeeNetPrice = this.model.sysParams.publishedNetForGrossProfit
      ? detail.feePublishedNetPrice
      : detail.feeNetPrice;
    let grossProfitPercentage = !this.model.sysParams.displayGrossProfitOnInquiry
      ? ''
      : feeTradePrice === '0.00'
      ? '0'
      : Math.trunc(((detail.feeTradePrice - gpFeeNetPrice) / detail.feeTradePrice) * 100).toFixed(0);

    let feeNetPriceDisplay = this.model.sysParams.displayNetOnPartsInquiry
      ? this.safeToFixed(detail.feePublishedNetPrice, 2)
      : '';

    if (this.model.hideAdditionalDetail) {
      feeNetPriceDisplay = '';
      grossProfitPercentage = '';
    }

    return (
      <>
        <tr className="sk-fee">
          <td />
          {contextMenuParams ? (
            <td>
              <MenuProvider event={contextMenuEvent} id={contextMenuParams.id}>
                &nbsp;
              </MenuProvider>
            </td>
          ) : (
            <td />
          )}
          <td />
          <td>
            <span title={detail.feeDescription}>{detail.feeDescription}</span>
          </td>
          <td />
          <td />
          <td />
          <td>
            <span title={feeNetPriceDisplay}>{feeNetPriceDisplay}</span>
          </td>
          <td>
            <span title={feeListPrice}>{feeListPrice}</span>
          </td>
          <td>
            <span title={feeTradePrice}>{feeTradePrice}</span>
          </td>
          <td>
            <span title={grossProfitPercentage}>{grossProfitPercentage}</span>
          </td>
          <td />
        </tr>
        {(this.props.model.suppressFees || this.props.lineItem.suppressFee) && (
          <tr className="sk-fee">
            <td />
            {contextMenuParams ? (
              <td>
                <MenuProvider event={contextMenuEvent} id={contextMenuParams.id}>
                  &nbsp;
                </MenuProvider>
              </td>
            ) : (
              <td />
            )}
            <td />
            <td>
              <span title={detail.feeDescription}>{detail.feeDescription}</span>
            </td>
            <td />
            <td />
            <td />
            <td />
            <td />
            <td>
              <span title={`-${feeTradePrice}`}>-{feeTradePrice}</span>
            </td>
            <td />
            <td />
          </tr>
        )}
      </>
    );
  }

  renderForceManagerOverrideModal() {
    return (
      this.showForceManagerOverrideModal && (
        <Modal
          backdrop="static"
          size="sm"
          container={getContainer}
          dialogClassName="sk-forceManagerOverrideModal"
          onHide={() => (this.showForceManagerOverrideModal = false)}
          show={this.showForceManagerOverrideModal}
        >
          <Modal.Header closeButton={true}>
            <Modal.Title>Manager Override</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="sk-managerPPCandPasswordContainer">
              <div className="sk-managerPPC">
                <Label>Manager PPC</Label>
                <input
                  autoFocus={true}
                  ref={(ref: HTMLInputElement) => (this.managerOverridePPCInput = ref)}
                  type="text"
                  style={{ width: '100%' }}
                />
              </div>
              <div className="sk-managerForcePassword">
                <Label>Force Password</Label>
                <input
                  autoFocus={true}
                  ref={(ref: HTMLInputElement) => (this.managerOverrideForcePasswordInput = ref)}
                  type="password"
                  style={{ width: '100%' }}
                  onKeyPress={evt => {
                    if (evt.key === 'Enter') {
                      this.validateManagerOverride();
                    }
                  }}
                />
              </div>
            </div>
            <FormCheck inline={true}>
              <FormCheck.Input
                type="checkbox"
                id="managerOverridePersist"
                className="sk-useMaterialInput ml-1"
                ref={(ref: HTMLInputElement) => (this.managerPersistForSessionCheckbox = ref)}
              />
              <FormCheck.Label htmlFor="managerOverridePersist">Persist for remainder of session?</FormCheck.Label>
            </FormCheck>
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={() => (this.showForceManagerOverrideModal = false)} variant="secondary">
              Cancel
            </Button>
            <Button onClick={() => this.validateManagerOverride()} variant="primary">
              Proceed
            </Button>
          </Modal.Footer>
        </Modal>
      )
    );
  }

  renderForcePasswordModal() {
    return (
      this.showForcePasswordModal && (
        <Modal
          backdrop="static"
          size="sm"
          container={getContainer}
          onHide={() => (this.showForcePasswordModal = false)}
          show={this.showForcePasswordModal}
        >
          <Modal.Header closeButton={true}>
            <Modal.Title>Force Password</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <input
              autoFocus={true}
              ref={(ref: HTMLInputElement) => (this.forcePasswordInput = ref)}
              type="password"
              style={{ width: '100%' }}
              onKeyPress={evt => {
                if (evt.key === 'Enter') {
                  this.validatePPCForcePassword();
                }
              }}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={() => (this.showForcePasswordModal = false)} variant="secondary">
              Cancel
            </Button>
            <Button onClick={() => this.validatePPCForcePassword()} variant="primary">
              Proceed
            </Button>
          </Modal.Footer>
        </Modal>
      )
    );
  }

  renderSerialNumbersSelectionModal() {
    const { lineItem } = this.props;

    return (
      this.showSerialNumbersSelectionModal && (
        <SerialNumbersSelectionModal
          container={getContainer}
          hide={() => (this.showSerialNumbersSelectionModal = false)}
          show={this.showSerialNumbersSelectionModal}
          serialsAvailable={lineItem.detail!.serialNumbersAvailable}
          serialsPreviouslySelected={lineItem.serialNumbersSelected}
          qtyRequired={lineItem.quantity}
          partNum={lineItem.detail!.partNum}
          apply={(serialNumbersSelected: string[]) => {
            lineItem.serialNumbersSelected = serialNumbersSelected;
            this.showSerialNumbersSelectionModal = false;
          }}
        />
      )
    );
  }

  renderInquiryMemos(ds: IDetailSupplemental) {
    return (
      ds &&
      ds.inquiryMemo.map((memo, index) => {
        memo = this.trimLegacyInquiryStrings(memo);
        return (
          <tr className="sk-lineItemD3QMcomment" key={index}>
            <td />
            <td />
            <td />
            <td colSpan={9}>
              <span title={memo}>{memo}</span>
            </td>
          </tr>
        );
      })
    );
  }

  renderLoyaltyCouponsApplied(detail: IPartLineDetail) {
    const { priceSymbol, stockClass } = this.model.loyaltyDefaultPart;
    const quantity = detail.loyaltyCouponsQuantity.toString();
    const offerTradePrice = detail.loyaltyCouponsPrice.toFixed(2);

    const couponCode = _.get(detail.loyaltyCouponsDisplayInfo.match(/LCOUPONCODE:(.*) LDESC/), 1, '');
    const couponDisplay = `COUPON CODE: ${couponCode}`.toUpperCase();
    const description = _.get(detail.loyaltyCouponsDisplayInfo.match(/LDESC:(.*)/), 1, '').trim();
    const offerId = _.get(detail.loyaltyCouponsDisplayInfo.match(/LOFFERID:(.*) LCOUPON/), 1, '');

    return (
      <>
        <tr className="sk-lineItemD3QMcomment">
          <td>
            <span title={quantity}>{quantity}</span>
          </td>
          <td>
            <span title="PACCAR LOYALTY COUPON">PACCAR LOYALTY COUPON</span>
          </td>
          <td />
          <td>
            <span title={description}>{description}</span>
          </td>
          <td />
          <td>
            <span title={priceSymbol}>{priceSymbol}</span>
          </td>
          <td>
            <span title={stockClass}>{stockClass}</span>
          </td>
          <td />
          <td />
          <td>
            <span title={offerTradePrice}>{offerTradePrice}</span>
          </td>
          <td />
          <td />
        </tr>
        {!this.model.hideAdditionalDetail && (
          <tr className="sk-lineItemD3QMcomment">
            <td />
            <td />
            <td />
            <td colSpan={9}>
              <div>
                <span title={couponDisplay}>{couponDisplay}</span>
              </div>
              <div onClick={() => modal.open('loyaltyOffer', { offerId, headerClass: 'primary' })}>
                <span className="sk-hyperLink" title="View Coupon Details">
                  CLICK HERE FOR MORE DETAILS
                </span>
              </div>
            </td>
          </tr>
        )}
      </>
    );
  }

  renderLoyaltyOffers(loyaltyOffers: ILoyaltyOffer[]) {
    const { priceSymbol, stockClass } = this.model.loyaltyDefaultPart;

    return loyaltyOffers.map((offer, offerIndex) => {
      const couponDisplay = `COUPON CODE: ${offer.CouponCode}`.toUpperCase();
      const description = offer.Title && typeof offer.Title === 'string' ? offer.Title.toUpperCase() : '';
      const offerTradePrice = this.safeToFixed(offer.Amount, 2);

      let quantity: number | string = this.props.lineItem.quantity; // defaults to line item qty unless partial redemption is proven
      let redemptionsTag = null;

      if (offer.Redemptions) {
        const redemptionCount = offer.Redemptions.length;
        const partialRedemption = redemptionCount < this.props.lineItem.quantity;

        redemptionsTag = (
          <div className="sk-loyaltyRedemptionBadge">
            <i className="zmdi zmdi-badge-check" /> {partialRedemption ? 'PARTIAL REDEMPTION' : 'REDEEMED'}
          </div>
        );

        if (partialRedemption) {
          quantity = redemptionCount;
        }
      }

      quantity = quantity.toString();

      return (
        <React.Fragment key={offerIndex}>
          <tr className="sk-lineItemD3QMcomment">
            <td>
              <span title={quantity}>{quantity}</span>
            </td>
            <td>
              <span title="PACCAR LOYALTY COUPON">PACCAR LOYALTY COUPON</span>
            </td>
            <td />
            <td>
              <span title={description}>{description}</span>
            </td>
            <td />
            <td>
              <span title={priceSymbol}>{priceSymbol}</span>
            </td>
            <td>
              <span title={stockClass}>{stockClass}</span>
            </td>
            <td />
            <td />
            <td>
              <span title={offerTradePrice}>{offerTradePrice}</span>
            </td>
            <td />
            <td>
              <span
                className="sk-removeItemIcon"
                onClick={() => {
                  this.activeCouponType = 'BONDLOYALTY';
                  this.activeCouponIndex = offerIndex;
                  this.showConfirmRemoveBoundCouponModal = true;
                }}
                title="Remove Attached Coupon from Line Item"
              >
                <i className="zmdi zmdi-delete zmdi-hc-lg" />
              </span>
            </td>
          </tr>
          {!this.model.hideAdditionalDetail && (
            <tr className="sk-lineItemD3QMcomment">
              <td />
              <td />
              <td />
              <td colSpan={9}>
                <div>
                  <span title={couponDisplay}>{couponDisplay}</span>
                </div>
                <div
                  onClick={() => modal.open('loyaltyOffer', { offerId: offer.OfferUniqueId, headerClass: 'primary' })}
                >
                  <span className="sk-hyperLink" title="View Coupon Details">
                    CLICK HERE FOR MORE DETAILS
                  </span>
                </div>
                {redemptionsTag}
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    });
  }

  // Supports editing an existing non-stocking part line item
  renderNonStockingPartDetailCell(value: string) {
    const editable = this.props.model.isLineItemCellEditable(this.props.itemIndex);

    return (
      <td
        onClick={() => {
          if (!this.isSMM3 && editable) {
            this.showNonStockingPartModal = true;
          }
        }}
        className={!this.isSMM3 && editable ? 'sk-editField' : ''}
      >
        <span title={value}>{value}</span>
      </td>
    );
  }

  renderOnHold(ds: IDetailSupplemental, contextMenuParams: IContextMenuParams | null) {
    if (this.props.lineItem.confirmationSale || this.props.lineItem.issuedToRO) {
      return null;
    }

    return (
      ds &&
      Array.isArray(ds.onHold) &&
      ds.onHold.map((item, index) => {
        const onHoldTitle =
          `HOLD: ${item.holdNumber} PO: ${item.purchaseOrder} Q:${item.quantity} ${item.holdDate}`.toUpperCase();
        const onHoldData = (
          <span title={onHoldTitle}>
            HOLD: {(item.holdNumber || '').toUpperCase()} PO:{' '}
            <span
              className="sk-hyperLink"
              onClick={() =>
                modal.open('purchaseOrderDetail', {
                  lookupKey: item.purchaseOrder,
                  title: `PO Detail: ${item.purchaseOrder}`,
                })
              }
            >
              {(item.purchaseOrder || '').toUpperCase()}
            </span>{' '}
            Q: {item.quantity} {item.holdDate}
          </span>
        );
        return (
          <tr className="sk-lineItemD3QMcomment" key={index}>
            <td />
            {contextMenuParams ? (
              <td>
                <MenuProvider event={contextMenuEvent} id={contextMenuParams.id}>
                  &nbsp;
                </MenuProvider>
              </td>
            ) : (
              <td />
            )}
            <td />
            <td colSpan={9}>{onHoldData}</td>
          </tr>
        );
      })
    );
  }

  renderOnOrder(ds: IDetailSupplemental, contextMenuParams: IContextMenuParams | null) {
    if (this.props.lineItem.confirmationSale || this.props.lineItem.issuedToRO) {
      return null;
    }

    return (
      ds &&
      Array.isArray(ds.onOrder) &&
      ds.onOrder.map((item, index) => {
        /* orderType: 'Customer', key custNumber will exist
           orderType: 'RepairOrder', key repairOrder will exist
           orderType: 'Return' and 'Stock', no additional details will exist
        */
        let onOrderSupplementalData = `${item.ppc} Q:${item.quantity} ${item.orderType}`;

        if (item.orderType === 'Customer' && item.custNumber !== '') {
          onOrderSupplementalData = `${onOrderSupplementalData}: ${item.custNumber}`;
        }

        if (item.orderType === 'RepairOrder' && item.repairOrder !== '') {
          onOrderSupplementalData = `${onOrderSupplementalData}: ${item.repairOrder}`;
        }

        if (item.date !== '') {
          onOrderSupplementalData = `${onOrderSupplementalData} ${item.date}`;
        }

        // Vendor back order flag
        const vendorBO = item.vendorBO === 'true' ? ' BO' : '';

        const onOrderTitle = `PO: ${item.purchaseOrder} ${onOrderSupplementalData}${vendorBO}`.toUpperCase();

        const onOrderData = (
          <span title={onOrderTitle}>
            PO:{' '}
            <span
              className="sk-hyperLink"
              onClick={() =>
                modal.open('purchaseOrderDetail', {
                  lookupKey: item.purchaseOrder,
                  title: `PO Detail: ${item.purchaseOrder}`,
                })
              }
            >
              {(item.purchaseOrder || '').toUpperCase()}
            </span>{' '}
            {onOrderSupplementalData.toUpperCase()}
            {vendorBO}
          </span>
        );

        return (
          <tr className="sk-lineItemD3QMcomment" key={index}>
            <td />
            {contextMenuParams ? (
              <td>
                <MenuProvider event={contextMenuEvent} id={contextMenuParams.id}>
                  &nbsp;
                </MenuProvider>
              </td>
            ) : (
              <td />
            )}
            <td />
            <td colSpan={9}>{onOrderData}</td>
          </tr>
        );
      })
    );
  }

  /* Used for standard stocking parts price fields.
     Renders NET (T2/T3,etc) / LIST / SELL, supports forcing where applicable (PIN1).
     Also used for price adjustments and pricing unpriced parts (PRO1/SRE2).
     PIN1 & PRO1 use the same force parts passwords, if configured.
  */
  renderPriceCell(value: string) {
    // isLineItemCellEditable means the line item is not on a locked/voided op on a service screen.
    const editable = this.props.model.isLineItemCellEditable(this.props.itemIndex) && !this.isPIN4 && !this.isSMM3;

    /* The clerk still needs permission to force stocking parts, the part must not explicitly
         disallow forcing, and the customer must not have STDP in their price codes.
         These checks are done in priceOverride().
  
         Though we check if forces are allowed in the model for highlighting the fields, we'll allow the
         clerk to still hit priceOverride on click if they're used to it, which will handle the notification.
      */
    return (
      <td
        onClick={() => {
          if (editable) {
            this.priceOverride();
          }
        }}
        className={classNames({ 'sk-editField': editable && this.model.forcePartsAllowed })}
      >
        <span title={value}>{value}</span>
      </td>
    );
  }

  /* Quantity field is rendered differently depending on if we're in edit mode
     or not.  Within render(), the 2nd param is a mobx observable, thus this
     method is called appropriately on observable changes.
  */
  renderEditableQuantityCell(quantity: string) {
    const { itemIndex, lineItem, model } = this.props;

    // PIN1 - Always editable
    // PRO1 NOT in Move Line Items Mode - Editable if not yet issued
    // PRO1 in Move Line Items Mode - Editable (splittable) only if already issued & qty > 1
    const moveOpSplitQtyEligible =
      model.moveOperationsModeActive &&
      lineItem.issuedToRO &&
      model.moveOperationsQueue.has(itemIndex) &&
      Number(quantity) > 1;

    const moveOpQtyOverride = moveOpSplitQtyEligible ? model.moveOperationsQueue.get(itemIndex) : undefined;
    if (moveOpQtyOverride) {
      quantity = moveOpQtyOverride.toString();
    }

    // Are we in an editing quantity state?
    if (this.quantityEditMode) {
      return (
        <td>
          <input
            className={classNames({
              'sk-moveOpSplitQtyEligible': moveOpSplitQtyEligible,
            })}
            defaultValue={quantity}
            onBlur={() => this.updateQuantity()}
            onKeyPress={evt => {
              if (evt.key === 'Enter') {
                this.updateQuantity();
              }
            }}
            ref={(ref: HTMLInputElement) => (this.quantityInput = ref)}
            style={{ lineHeight: 1, width: '100%' }}
            type="text"
          />
        </td>
      );

      // Otherwise, not in edit mode, simply render quantity to grid
    } else {
      const editable = model.moveOperationsModeActive ? moveOpSplitQtyEligible : !lineItem.issuedToRO;

      // Do not nest any children under the innermost div. InvoiceBuilder.tsx depends on this structure
      // for split qty edits to work when in move line items mode.
      return (
        <td>
          <div
            title={moveOpSplitQtyEligible ? 'Enter the quantity to move to new operation.' : quantity}
            className={classNames({
              'sk-editField': editable,
              'sk-moveOpSplitQtyEligible': moveOpSplitQtyEligible,
              'sk-moveOpQtyOverride': moveOpQtyOverride && moveOpQtyOverride < lineItem.quantity,
            })}
            onClick={() => {
              if (!editable) {
                // parts issued to ROs are not quantity editable, for example
                return;
              } else if (model.isLineFetching()) {
                toastr.error('Wait', 'Fetch action in process. Please try again in a moment.');
              } else {
                this.quantityEditMode = true;
              }
            }}
          >
            {quantity}
          </div>
        </td>
      );
    }
  }

  renderRemoveLineItemBoundCouponModal() {
    const itemName = `${this.props.lineItem.partNum} Coupon(${this.activeCouponIndex + 1})`;

    return (
      this.showConfirmRemoveBoundCouponModal && (
        <Modal
          backdrop="static"
          size="sm"
          container={getContainer}
          onHide={() => (this.showConfirmRemoveBoundCouponModal = false)}
          show={this.showConfirmRemoveBoundCouponModal}
        >
          <Modal.Header closeButton={true}>
            <Modal.Title>Confirm Remove</Modal.Title>
          </Modal.Header>
          <Modal.Body>Remove {itemName} ?</Modal.Body>
          <Modal.Footer>
            <Button onClick={() => (this.showConfirmRemoveBoundCouponModal = false)} variant="secondary">
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                this.model.removeLineItemCoupon(this.props.itemIndex, this.activeCouponIndex, this.activeCouponType);
                this.showConfirmRemoveBoundCouponModal = false;
              }}
            >
              Remove
            </Button>
          </Modal.Footer>
        </Modal>
      )
    );
  }

  // Remove line item confirmation Modal
  renderRemoveLineItemModal() {
    const itemName = this.props.lineItem.comment
      ? 'Comment/Memo'
      : this.props.lineItem.defaultPartNum
      ? this.props.lineItem.defaultPartNum
      : this.props.lineItem.nonStockingPartNum
      ? `@${this.props.lineItem.nonStockingPartNum}`
      : this.props.lineItem.partNum;

    if (['PRO1', 'SMM3', 'SRE2'].includes(this.props.model.runMode) && this.props.lineItem.issuedToRO) {
      return (
        this.showConfirmRemoveModal && (
          <RemoveROLineItemModal
            container={getContainer}
            model={this.model}
            hide={() => (this.showConfirmRemoveModal = false)}
            itemName={itemName}
            lineItem={this.props.lineItem}
            show={this.showConfirmRemoveModal}
          />
        )
      );
    }

    return (
      this.showConfirmRemoveModal && (
        <Modal
          backdrop="static"
          size="sm"
          container={getContainer}
          onHide={() => (this.showConfirmRemoveModal = false)}
          show={this.showConfirmRemoveModal}
        >
          <Modal.Header closeButton={true}>
            <Modal.Title>Confirm Remove</Modal.Title>
          </Modal.Header>
          <Modal.Body>Remove {itemName} ?</Modal.Body>
          <Modal.Footer>
            <Button onClick={() => (this.showConfirmRemoveModal = false)} variant="secondary">
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                this.model.removeLineItem(this.props.itemIndex);
                this.showConfirmRemoveModal = false;
              }}
            >
              Remove
            </Button>
          </Modal.Footer>
        </Modal>
      )
    );
  }

  renderOpLine() {
    const lineItem = this.props.lineItem;
    if (lineItem.roDetail) {
      return (
        <span style={{ marginRight: '7px' }}>
          {`${lineItem.roDetail.operationNumber}.${lineItem.roDetail.lineNumber}`}
        </span>
      );
    } else {
      return null;
    }
  }

  renderSerialRequired() {
    const { lineItem, model } = this.props;

    if (lineItem.issuedToRO || !lineItem.detail!.serialNumberRequired) {
      return null;
    }

    return (
      <tr
        className={`sk-lineItemSerialNumbers${
          lineItem.serialNumbersSelected && lineItem.serialNumbersSelected.length === lineItem.quantity
            ? ' sk-lineItemSerialNumbersSelected'
            : ''
        }`}
      >
        <td />
        <td colSpan={11} onContextMenu={evt => evt.preventDefault()}>
          <span
            className="sk-lineItemSerialNumbersContainer"
            onClick={() => {
              this.showSerialNumbersSelectionModal = true;
            }}
          >
            {!lineItem.serialNumbersSelected || lineItem.serialNumbersSelected.length !== lineItem.quantity ? (
              <span>
                Serial number required to {model.runMode === 'PIN1' ? 'sell' : 'issue'} this item. Click or Tap here.
              </span>
            ) : (
              <span>
                Serial #:
                {lineItem.serialNumbersSelected.map((serialNumber, i) => (
                  <div key={`serialNumber-${i}`} className="sk-serial-number">
                    {serialNumber}
                  </div>
                ))}
              </span>
            )}
          </span>
        </td>
      </tr>
    );
  }

  render() {
    const { isDragging, itemIndex, lineItem, model } = this.props;

    // We filter by active operation here so the invoice builder can continue to easily track line positions.
    // ALL mode, signified by active operation 0. This is essentially a visibility: hidden equivalence filter.
    if (['PRO1', 'SMM3', 'SRE2'].includes(model.runMode)) {
      if (
        !(
          (this.model.activeOperation === 0 && lineItem.issuedToRO) ||
          (lineItem.roDetail && lineItem.roDetail.operationNumber === this.model.activeOperation)
        )
      ) {
        return null;
      }
    }

    const connectDragPreview = this.props.connectDragPreview as Function;
    const connectDragSource = this.isDraggingEnabled ? (this.props.connectDragSource as Function) : (x: any) => x;
    const connectDropTarget = this.props.connectDropTarget as Function;
    const dragHandleClass = this.dragHandleClass;

    let renderedComponent: JSX.Element;

    // This only applies to stocking line items and stocking line items with supercedence trails.
    const PartContextMenu = ({
      id,
      lookupKey,
      nonStocking,
    }: {
      id: string;
      lookupKey: string;
      nonStocking?: boolean;
    }) => {
      const coreSuppressionEligible =
        lineItem.detail &&
        lineItem.detail.coreAttached &&
        model.sysParams.coreChargeSuppression &&
        model.runMode === 'PIN1';
      const feeSuppressionEligible =
        lineItem.detail && lineItem.detail.feeAttached && model.sysParams.feeSuppression && model.runMode === 'PIN1';

      if (this.isSMM3) {
        return (
          <Menu id={id} animation="fade">
            <Item
              onClick={() => {
                modal.open('attachments', {
                  itemId: lookupKey,
                  itemName: 'PIV1',
                  readOnly: true,
                });
              }}
            >
              View Attachments
            </Item>
          </Menu>
        );
      }

      if (nonStocking) {
        return (
          <Menu id={id} animation="fade">
            <Item
              onClick={() => {
                modal.open('partHistory', { lookupKey });
              }}
            >
              Part History
            </Item>
          </Menu>
        );
      }

      return (
        <Menu id={id} animation="fade">
          <Item
            onClick={() => {
              modal.open('partDetail', { lookupKey });
            }}
          >
            Part Detail
          </Item>
          <Item
            onClick={() => {
              modal.open('partHistory', { lookupKey });
            }}
          >
            Part History
          </Item>
          {this.props.model.sysParams.fleetcross && (
            <Item
              onClick={() => {
                this.props.fleetcrossModal(lookupKey, true);
              }}
            >
              Fleetcross Inquiry
            </Item>
          )}
          <Item
            onClick={() => {
              modal.open('attachments', {
                itemId: lookupKey,
                itemName: 'PIV1',
                readOnly: true,
              });
            }}
          >
            View Attachments
          </Item>
          {(coreSuppressionEligible || feeSuppressionEligible) && <Separator />}
          {coreSuppressionEligible && (
            <Item
              disabled={model.suppressCoreCharges}
              onClick={() => model.suppressLineItemCoreCharge(itemIndex, !lineItem.suppressCoreCharge)}
            >
              {lineItem.suppressCoreCharge && '✓ '}Remove Core Charge
            </Item>
          )}
          {feeSuppressionEligible && (
            <Item
              disabled={model.suppressFees}
              onClick={() => model.suppressLineItemFee(itemIndex, !lineItem.suppressFee)}
            >
              {lineItem.suppressFee && '✓ '}Remove Fee
            </Item>
          )}
        </Menu>
      );
    };

    // PRO1/SMM3/SRE2 only
    const lineItemSelectedForMoveOperation = model.moveOperationsModeActive && model.moveOperationsQueue.has(itemIndex);

    // Is this a memo/comment line? (No detail fetching check required.)
    if (lineItem.comment) {
      const titleOverride = lineItem.confirmationComment
        ? `${lineItem.comment} CONFIRMATION:${lineItem.confirmationComment.confirmationNum} ${lineItem.confirmationComment.poNum}`
        : null;

      renderedComponent = (
        <div
          className={classNames({
            'sk-lineItemColsHoverable': !isDragging,
            'sk-lineItemConfirmationSale': lineItem.confirmationComment && !lineItem.issuedToRO,
            'sk-lineItemIssuedToRO': lineItem.issuedToRO,
            'sk-lineItemSelectedForMoveOperation': lineItemSelectedForMoveOperation,
          })}
        >
          <table className="sk-lineItemCols">
            <tbody>
              <tr className="sk-lineItemComment">
                {connectDragSource(this.renderCommentCell(lineItem.comment, titleOverride))}
                <td>
                  {!this.isSMM3 && (
                    <span
                      className="sk-removeItemIcon"
                      onClick={() => {
                        if (['PRO1', 'SMM3', 'SRE2'].includes(model.runMode) && lineItem.issuedToRO) {
                          this.showConfirmRemoveModal = true;
                        } else {
                          model.removeLineItem(itemIndex);
                        }
                      }}
                      title="Remove Line Item"
                    >
                      <i className="zmdi zmdi-delete zmdi-hc-lg" />
                    </span>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
          {this.renderRemoveLineItemModal()}
        </div>
      );

      // Are we waiting for DBPIP to fill in the details?
    } else if (!lineItem.fetched) {
      renderedComponent = (
        <div className="sk-fetching">
          {lineItem.quantity}.{lineItem.partNum} -{' '}
          {lineItem.extendedFetchingDetails ? lineItem.extendedFetchingDetails : 'Fetching'} ...
        </div>
      );

      /* Is this a non-fleet billing supercedence case?  If so, we need to display the supercedence path
       and disable features such as part swaps and forces.  This is due to the fact the billed invoice
       must show the original requested part and bill the supers accordingly. Forces would not allow that.
       If a parts manager wants to override the resultant parts, he can add them directly.  Note, fleet
       billed customers do not hit this case, rather parts are simply subsituted in the invoice builder
       and can be forced as usual.  The preceeding if checks are a TS typeguard for type inference.

       This render path is different enough from "standard" parts that we cannot extract a shared method.
    */
    } else if (
      lineItem.supercedenceDetails &&
      lineItem.supercedenceDetailsSupplemental &&
      lineItem.detailSupplemental
    ) {
      const quantity = this.safeToFixed(lineItem.quantity, 0); // qty of the "base" supercedence part (possibly NaN)

      // Each supercenceDetail line is a tail "constituent", the quantity of which has possibly been pre-escalated by DBPIP
      const onHandZeroOrNegative = _.some(lineItem.supercedenceDetails, (o: IPartLineDetail) => {
        const constituentQuantity = this.safeToFixed(o.quantity, 0); // (possibly NaN)
        const constituentQuantityInt = parseInt(constituentQuantity, 10);
        const onHand = o.priceSymbol.endsWith('3')
          ? constituentQuantity
          : (_.get(lineItem.supercedenceDetailsSupplemental, `${o.partNum}.onHand`, '') as string);
        const onHandInt = parseInt(onHand, 10);

        return onHand !== '' && constituentQuantity !== '' && (onHandInt <= 0 || onHandInt < constituentQuantityInt);
      });

      // only used if part alternatives exist
      const expandIcon = `zmdi zmdi-hc-lg ${
        model.expandAllAlternatives || lineItem.showPartAlternatives ? 'zmdi-caret-up-circle' : 'zmdi-caret-down-circle'
      }`;
      const basePartDetailSupplemental = lineItem.detailSupplemental; // original superceded partNum details
      const basePartAlternativesExist =
        basePartDetailSupplemental &&
        (basePartDetailSupplemental.alternateDivisions.length !== 0 ||
          basePartDetailSupplemental.branchInventory.length !== 0 ||
          basePartDetailSupplemental.substitutions.length !== 0);
      const constituentPartsAlternativesExist = Object.keys(lineItem.supercedenceDetailsSupplemental).some(o => {
        const constituentDs = lineItem.supercedenceDetailsSupplemental![o];
        return (
          constituentDs.alternateDivisions.length !== 0 ||
          constituentDs.branchInventory.length !== 0 ||
          constituentDs.substitutions.length !== 0
        );
      });

      // We show 1 expansion icon if either case is valid
      const partAlternativesExist = basePartAlternativesExist || constituentPartsAlternativesExist;

      const splitSupercedenceCase = lineItem.supercedenceDetails.length > 1;
      const nonSplitEscalatedQtyCase =
        lineItem.supercedenceDetails.length === 1 && lineItem.quantity !== lineItem.supercedenceDetails[0].quantity;

      renderedComponent = (
        <div
          className={classNames({
            'sk-lineItemColsHoverable': !isDragging,
            'sk-zeroOrNegativeOnHand': onHandZeroOrNegative,
          })}
        >
          <table className="sk-lineItemCols">
            <tbody>
              {/* Render the root supercedence part along with supercedence comments and a button to remove the tree */}
              <tr className="sk-supercedenceHeader">
                {this.renderEditableQuantityCell(quantity)}
                {connectDragSource(
                  <td className={dragHandleClass}>
                    <MenuProvider event={contextMenuEvent} id={this.props.itemIndex.toString()}>
                      {partAlternativesExist &&
                        !model.hideAdditionalDetail &&
                        !lineItem.confirmationSale &&
                        !lineItem.issuedToRO && (
                          <span
                            className="sk-showPartAlternatives"
                            title="Show More Part Options"
                            onClick={evt => {
                              if (
                                partAlternativesExist &&
                                !model.expandAllAlternatives &&
                                !model.hideAdditionalDetail
                              ) {
                                lineItem.showPartAlternatives = !lineItem.showPartAlternatives;
                              }
                            }}
                          >
                            <i className={expandIcon} />
                          </span>
                        )}
                      <span title={lineItem.partNum} onDoubleClick={() => this.copyToClipboard(lineItem.partNum)}>
                        {lineItem.issuedToRO ? this.renderOpLine() : ''}
                        {lineItem.partNum}
                      </span>
                      {_.times(lineItem.detailSupplemental.supercedenceComments.length - 1, i => (
                        <React.Fragment key={`sp${i}`}>
                          <br />
                          &nbsp;
                        </React.Fragment>
                      ))}
                    </MenuProvider>
                    <PartContextMenu id={this.props.itemIndex.toString()} lookupKey={lineItem.partNum} />
                  </td>
                )}
                <td />
                <td className="sk-supercedenceComment">
                  {lineItem.detailSupplemental.supercedenceComments.map((comment, index) => (
                    <div key={index} title={comment}>
                      {comment}
                    </div>
                  ))}
                </td>
                <td>
                  <span
                    className="sk-removeItemIcon"
                    onClick={() => (this.showConfirmRemoveModal = true)}
                    title="Remove Line Item"
                  >
                    <i className="zmdi zmdi-delete zmdi-hc-lg" />
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          {basePartAlternativesExist &&
            this.renderCollapsableAlternatives({ detailSupplemental: basePartDetailSupplemental })}

          {/* Render the supercedence constituent lines here */}
          {lineItem.supercedenceDetails.map((supercedenceDetail, index) => {
            const supercedenceDetailSupplemental = _.get(
              lineItem.supercedenceDetailsSupplemental,
              `${supercedenceDetail.partNum}`
            );

            /* Extract the "USE" supercedence lines from the error text (possible supercedence "trails" A->B->C).
               Context menu modals are available for every part in the supercedence trail, not just the tail constituents.
               We only show attachment indicators and images for tail constitutents, though (e.g. not B).
            */
            let contextMenuParams: IContextMenuParams | null = null;
            const useLines = supercedenceDetail.errorText.map((useLine, index2) => {
              if (useLine.startsWith('USE ')) {
                const usePartNum = useLine.match(/^USE[ ]+(\d{1,4}\.)?(.+)$/);
                if (usePartNum) {
                  const id = `${this.props.itemIndex}-${index}-${index2}`;
                  const lookupKey = usePartNum[2];
                  contextMenuParams = { id, lookupKey };
                  return (
                    <span key={index2}>
                      <MenuProvider event={contextMenuEvent} id={id}>
                        <span title={useLine}>
                          {lookupKey === supercedenceDetail.partNum &&
                            lineItem.supercedenceAttachments &&
                            lineItem.supercedenceAttachments[supercedenceDetail.partNum] &&
                            lineItem.supercedenceAttachments[supercedenceDetail.partNum].length > 0 && (
                              <span
                                className="sk-attachmentsIndicator"
                                title={`Show ${
                                  lineItem.supercedenceAttachments[supercedenceDetail.partNum].length
                                } Attachments`}
                                onClick={() => {
                                  modal.open('attachments', {
                                    itemId: supercedenceDetail.partNum,
                                    itemName: 'PIV1',
                                    readOnly: true,
                                  });
                                }}
                              >
                                <i className="zmdi zmdi-attachment-alt" />
                              </span>
                            )}
                          {useLine}
                          <br />
                        </span>
                      </MenuProvider>
                      <PartContextMenu id={id} lookupKey={lookupKey} />
                    </span>
                  );
                } else {
                  // Unexpected USE line format
                  console.error('Unexpected USE format:');
                  console.log(useLine);
                  contextMenuParams = null;
                  return (
                    <span title={useLine} key={index2}>
                      {useLine}
                      <br />
                    </span>
                  );
                }
              }
            });

            const constituentQuantity = this.safeToFixed(supercedenceDetail.quantity, 0); // (possibly NaN)
            const constituentQuantityInt = parseInt(constituentQuantity, 10);
            const onHand = supercedenceDetail.priceSymbol.endsWith('3')
              ? constituentQuantity
              : _.get(supercedenceDetailSupplemental, 'onHand', '');
            const onHandInt = parseInt(onHand, 10);
            const description = supercedenceDetail.shortDescription
              ? supercedenceDetail.shortDescription
              : supercedenceDetail.longDescription;
            const binLocation = supercedenceDetail.binLocation.join(',');
            const stockClassName = _.get(supercedenceDetailSupplemental, 'stockClassName', '');
            const listPrice = this.safeToFixed(supercedenceDetail.listPrice, 2);
            const tradePrice = this.safeToFixed(supercedenceDetail.tradePrice, 2);

            const constituentPartAlternativesExist =
              supercedenceDetailSupplemental &&
              (supercedenceDetailSupplemental.alternateDivisions.length !== 0 ||
                supercedenceDetailSupplemental.branchInventory.length !== 0 ||
                supercedenceDetailSupplemental.substitutions.length !== 0);

            // Based on FIFO net of the constituent unless PTB2 is set to use published net in GP calcs
            const sdNetPrice = this.model.sysParams.publishedNetForGrossProfit
              ? supercedenceDetail.publishedNetPrice
              : supercedenceDetail.netPrice;
            let grossProfitPercentage = !model.sysParams.displayGrossProfitOnInquiry
              ? ''
              : tradePrice === '0.00'
              ? '0'
              : Math.trunc(
                  ((supercedenceDetail.tradePrice - sdNetPrice) / supercedenceDetail.tradePrice) * 100
                ).toFixed(0);

            // InvoiceBuilder will automatically show the right column header based on the type of net price setup on PTB2
            // Here we use the correct dollar value for the determined "net price" (net / trade2 / trade3).
            // Additionally, the "displayNetOnPartsInquiry" setting ONLY applies when the switch uses NET pricing.
            let netPriceDisplay = '';
            switch (model.sysParams.partsInquiryDisplayOptions) {
              case 'N':
                netPriceDisplay = model.sysParams.displayNetOnPartsInquiry
                  ? this.safeToFixed(supercedenceDetail.publishedNetPrice, 2)
                  : '';
                break;
              case 'T2':
                netPriceDisplay = this.safeToFixed(supercedenceDetail.trade2Price, 2);
                break;
              case 'T3':
                netPriceDisplay = this.safeToFixed(supercedenceDetail.trade3Price, 2);
                break;
              default:
                // 'W', 'X', or nothing
                break;
            }

            if (model.hideAdditionalDetail) {
              grossProfitPercentage = '';
              netPriceDisplay = '';
            }

            // Follows sk-lineItemCols layout
            return (
              <React.Fragment key={index}>
                <table className="sk-lineItemCols">
                  <tbody>
                    <tr className="sk-supercedenceDetail">
                      <td />
                      <td>{useLines}</td>
                      <td>
                        <span
                          title={onHand}
                          className={classNames({
                            'sk-onHand':
                              onHand !== '' &&
                              constituentQuantity !== '' &&
                              (onHandInt <= 0 || onHandInt < constituentQuantityInt),
                          })}
                        >
                          {onHand}
                        </span>
                      </td>
                      <td>
                        <span title={description}>{description}</span>
                      </td>
                      <td>
                        <span title={binLocation}>{binLocation}</span>
                      </td>
                      <td>
                        <span title={stockClassName || supercedenceDetail.stockClass}>
                          {supercedenceDetail.stockClass}
                        </span>
                      </td>
                      <td>
                        <span title={supercedenceDetail.priceSymbol}>{supercedenceDetail.priceSymbol}</span>
                      </td>
                      <td>
                        <span title={netPriceDisplay}>{netPriceDisplay}</span>
                      </td>
                      <td>
                        <span title={listPrice}>{listPrice}</span>
                      </td>
                      <td>
                        <span title={tradePrice}>{tradePrice}</span>
                      </td>
                      <td>
                        <span title={grossProfitPercentage}>{grossProfitPercentage}</span>
                      </td>
                      <td />
                    </tr>

                    {supercedenceDetail.coreAttached &&
                      this.renderCoreCharge(supercedenceDetail, supercedenceDetailSupplemental!, contextMenuParams)}
                    {supercedenceDetail.feeAttached && this.renderFee(supercedenceDetail, contextMenuParams)}
                    {!model.hideAdditionalDetail && this.renderD3QMComment(supercedenceDetail, contextMenuParams)}
                    {!model.hideAdditionalDetail &&
                      this.renderOnOrder(supercedenceDetailSupplemental!, contextMenuParams)}
                    {!model.hideAdditionalDetail &&
                      this.renderOnHold(supercedenceDetailSupplemental!, contextMenuParams)}
                    {/* {!model.hideAdditionalDetail && this.renderInquiryMemos(supDetailSupplemental as IDetailSupplemental)} */}
                    {!model.hideAdditionalDetail && this.renderErrors(supercedenceDetail, contextMenuParams)}
                  </tbody>
                </table>

                {constituentPartAlternativesExist &&
                  this.renderCollapsableAlternatives({
                    detailSupplemental: supercedenceDetailSupplemental!,
                    suppressHotSwap: splitSupercedenceCase || nonSplitEscalatedQtyCase,
                    suppressHotSwapMsg: splitSupercedenceCase
                      ? 'Substitution hot-swap not available in split supercedence cases.'
                      : 'Substitution hot-swap not available in quantity escalated supercedence cases.',
                  })}

                {model.showAttachmentsThumbnails &&
                  lineItem.supercedenceAttachments &&
                  lineItem.supercedenceAttachments[supercedenceDetail.partNum] &&
                  this.renderAttachments(
                    supercedenceDetail.partNum,
                    lineItem.supercedenceAttachments[supercedenceDetail.partNum]
                  )}
              </React.Fragment>
            );
          })}

          {this.renderRemoveLineItemModal()}

          {/* Render any attached coupons */}
          {lineItem.couponDetail!.length > 0 && (
            <>
              <table className="sk-lineItemCols">
                <tbody>{this.renderCoupons(lineItem.couponDetail!)}</tbody>
              </table>
              {/* All coupons share the same remove bound coupon modal in the DOM */}
              {this.renderRemoveLineItemBoundCouponModal()}
            </>
          )}
        </div>
      );

      // Standard line item eligible for force overrides
    } else if (lineItem.detail) {
      // Cleanup possible NaNs and such
      const quantity = this.safeToFixed(lineItem.quantity, 0);
      const stockClassName = lineItem.detailSupplemental ? lineItem.detailSupplemental.stockClassName : '';
      const binLocation = lineItem.detail.binLocation.join(',');
      let listPrice = this.safeToFixed(lineItem.detail.listPrice, 2);
      let netPrice = this.safeToFixed(lineItem.detail.netPrice, 2); // FIFO net unless overridden with force below
      let tradePrice = this.safeToFixed(lineItem.detail.tradePrice, 2);
      const publishedNetPrice = this.safeToFixed(lineItem.detail.publishedNetPrice, 2); // never overridden

      let onHand = '';
      // on-hand not displayed on confirmation sales or parts already issued to an RO (where inventory would've been relieved)
      if (!lineItem.confirmationSale && !lineItem.issuedToRO) {
        // inexhaustible part?  on-hand = qty requested
        if (lineItem.detail.priceSymbol.endsWith('3')) {
          onHand = quantity;
        } else if (lineItem.detailSupplemental) {
          onHand = lineItem.detailSupplemental.onHand;
        }
      }

      // Based on FIFO net of the part unless PTB2 is set to use published net in GP calcs
      const gpNetPrice = this.model.sysParams.publishedNetForGrossProfit
        ? lineItem.detail.publishedNetPrice
        : lineItem.detail.netPrice;
      let grossProfitPercentage =
        tradePrice === '0.00'
          ? '0'
          : Math.trunc(((lineItem.detail.tradePrice - gpNetPrice) / lineItem.detail.tradePrice) * 100).toFixed(0);

      // Apply forced pricing, if it exists
      if (lineItem.forcedPricing) {
        listPrice = this.safeToFixed(lineItem.forcedListPrice!, 2);
        netPrice = this.safeToFixed(lineItem.forcedNetPrice!, 2);
        tradePrice = this.safeToFixed(lineItem.forcedTradePrice!, 2);

        grossProfitPercentage =
          tradePrice === '0.00'
            ? '0'
            : Math.trunc(
                ((lineItem.forcedTradePrice! - lineItem.forcedNetPrice!) / lineItem.forcedTradePrice!) * 100
              ).toFixed(0);
      }

      grossProfitPercentage = model.sysParams.displayGrossProfitOnInquiry ? grossProfitPercentage : '';

      // InvoiceBuilder will automatically show the right column header based on the type of net price setup on PTB2
      // Here we use the correct dollar value for the determined "net price" (net / trade2 / trade3).
      // In the event of forced pricing, T2/T3 settings should show the forced trade price per PIN1 (odd).
      // Additionally, the "displayNetOnPartsInquiry" setting ONLY applies when the switch uses NET pricing.
      let netPriceDisplay = '';
      switch (model.sysParams.partsInquiryDisplayOptions) {
        case 'N':
          netPriceDisplay = model.sysParams.displayNetOnPartsInquiry
            ? lineItem.forcedPricing
              ? netPrice
              : publishedNetPrice
            : '';
          break;
        case 'T2':
          netPriceDisplay = lineItem.forcedPricing ? tradePrice : this.safeToFixed(lineItem.detail.trade2Price, 2);
          break;
        case 'T3':
          netPriceDisplay = lineItem.forcedPricing ? tradePrice : this.safeToFixed(lineItem.detail.trade3Price, 2);
          break;
        default:
          // 'W', 'X', or nothing
          break;
      }

      if (model.hideAdditionalDetail) {
        grossProfitPercentage = '';
        netPriceDisplay = '';
      }

      let onHandZeroOrNegative = false;
      if (onHand !== '') {
        const onHandInt = parseInt(onHand, 10);
        const quantityInt = parseInt(quantity, 10);

        // 1. Line item has 0 or negative qty on hand?
        // 2. Will the requested quantity cause the part stock to go negative?
        // (1 || 2): Style line item with pink background and red/bold on hand value.
        if (onHandInt <= 0 || onHandInt < quantityInt) {
          onHandZeroOrNegative = true;
        }
      }

      // only used if part alternatives exist
      const expandIcon = `zmdi zmdi-hc-lg ${
        model.expandAllAlternatives || lineItem.showPartAlternatives ? 'zmdi-caret-up-circle' : 'zmdi-caret-down-circle'
      }`;
      const detailSupplemental = lineItem.detailSupplemental;
      const partAlternativesExist =
        detailSupplemental &&
        (detailSupplemental.alternateDivisions.length !== 0 ||
          detailSupplemental.branchInventory.length !== 0 ||
          detailSupplemental.substitutions.length !== 0);

      const id = this.props.itemIndex.toString();
      const lookupKey = lineItem.partNum;
      const contextMenuParams = { id, lookupKey };
      const partTitle = lineItem.confirmationSale
        ? `${lineItem.partNum} CONFIRMATION:${lineItem.confirmationSale.confirmationNum} ${lineItem.confirmationSale.poNum}`
        : lineItem.partNum;

      renderedComponent = (
        <div
          className={classNames({
            'sk-lineItemColsHoverable': !isDragging,
            'sk-zeroOrNegativeOnHand': onHandZeroOrNegative,
            'sk-lineItemConfirmationSale': lineItem.confirmationSale && !lineItem.issuedToRO,
            'sk-lineItemIssuedToRO': lineItem.issuedToRO,
            'sk-lineItemSelectedForMoveOperation': lineItemSelectedForMoveOperation,
          })}
        >
          <table className="sk-lineItemCols">
            <tbody>
              <tr>
                {this.renderEditableQuantityCell(quantity)}
                {connectDragSource(
                  <td className={dragHandleClass}>
                    <MenuProvider event={contextMenuEvent} id={id}>
                      {partAlternativesExist &&
                        !model.hideAdditionalDetail &&
                        !lineItem.confirmationSale &&
                        !lineItem.issuedToRO && (
                          <span
                            className="sk-showPartAlternatives"
                            title="Show More Part Options"
                            onClick={evt => {
                              if (
                                partAlternativesExist &&
                                !model.expandAllAlternatives &&
                                !model.hideAdditionalDetail
                              ) {
                                lineItem.showPartAlternatives = !lineItem.showPartAlternatives;
                              }
                            }}
                          >
                            <i className={expandIcon} />
                          </span>
                        )}
                      <span title={partTitle} onDoubleClick={() => this.copyToClipboard(lineItem.partNum)}>
                        {lineItem.issuedToRO ? this.renderOpLine() : ''}
                        {lineItem.attachments && lineItem.attachments.length > 0 && (
                          <span
                            className="sk-attachmentsIndicator"
                            title={`Show ${lineItem.attachments.length} Attachments`}
                            onClick={() => {
                              modal.open('attachments', {
                                itemId: lineItem.partNum,
                                itemName: 'PIV1',
                                readOnly: true,
                              });
                            }}
                          >
                            <i className="zmdi zmdi-attachment-alt" />
                          </span>
                        )}
                        {lineItem.partNum}
                      </span>
                    </MenuProvider>
                    <PartContextMenu id={id} lookupKey={lookupKey} />
                  </td>
                )}
                <td>
                  <span title={onHand} className="sk-onHand">
                    {onHand}
                  </span>
                </td>
                {this.renderStockingPartDescriptionCell(this.activeDescription(), lineItem.detail.longDescription)}
                <td>
                  <span title={binLocation}>{binLocation}</span>
                </td>
                <td>
                  <span title={stockClassName || lineItem.detail.stockClass}>{lineItem.detail.stockClass}</span>
                </td>
                <td>
                  <span title={lineItem.detail.priceSymbol}>{lineItem.detail.priceSymbol}</span>
                </td>
                {this.renderPriceCell(netPriceDisplay)}
                {this.renderPriceCell(listPrice)}
                {this.renderPriceCell(tradePrice)}
                <td>
                  <span title={grossProfitPercentage}>{grossProfitPercentage}</span>
                </td>
                <td>
                  {!this.isSMM3 && (
                    <span
                      className="sk-removeItemIcon"
                      onClick={() => (this.showConfirmRemoveModal = true)}
                      title="Remove Line Item"
                    >
                      <i className="zmdi zmdi-delete zmdi-hc-lg" />
                    </span>
                  )}
                </td>
              </tr>

              {/* We only require serial numbers when selling or issuing.  Pick lists, quotes, and estimates won't use it. */}
              {['PIN1', 'PRO1'].includes(model.runMode) &&
                !lineItem.issuedToRO &&
                lineItem.detail.serialNumberRequired &&
                this.renderSerialRequired()}

              {lineItem.detail.coreAttached &&
                this.renderCoreCharge(lineItem.detail, lineItem.detailSupplemental!, contextMenuParams)}
              {lineItem.detail.feeAttached && this.renderFee(lineItem.detail, contextMenuParams)}
              {!model.hideAdditionalDetail && this.renderD3QMComment(lineItem.detail, contextMenuParams)}
              {!model.hideAdditionalDetail &&
                this.renderOnOrder(lineItem.detailSupplemental as IDetailSupplemental, contextMenuParams)}
              {!model.hideAdditionalDetail &&
                this.renderOnHold(lineItem.detailSupplemental as IDetailSupplemental, contextMenuParams)}
              {/* {!model.hideAdditionalDetail && this.renderInquiryMemos(lineItem.detailSupplemental as IDetailSupplemental)} */}
              {!model.hideAdditionalDetail && this.renderErrors(lineItem.detail, contextMenuParams)}
              {/* Previously redeemed and issued loyalty coupons */}
              {lineItem.detail.loyaltyCouponsApplied && this.renderLoyaltyCouponsApplied(lineItem.detail)}
              {/* Render any attached coupons - Original Google Big Query Based Program */}
              {lineItem.couponDetail!.length > 0 && this.renderCoupons(lineItem.couponDetail!)}
              {/* Render any attached coupons - PACCAR/Bond Based Program */}
              {Array.isArray(lineItem.loyaltyOffers) && this.renderLoyaltyOffers(lineItem.loyaltyOffers)}
            </tbody>
          </table>

          {partAlternativesExist && this.renderCollapsableAlternatives({ detailSupplemental: detailSupplemental! })}

          {model.showAttachmentsThumbnails &&
            lineItem.attachments &&
            lineItem.attachments.length > 0 &&
            this.renderAttachments(lineItem.partNum, lineItem.attachments)}

          {this.renderRemoveLineItemModal()}
          {this.renderForcePasswordModal()}
          {this.renderForceManagerOverrideModal()}

          {/* All coupons share the same remove bound coupon modal in the DOM */}
          {/* Supports original coupon program & new (2020 PACCAR Loyalty Bond) program */}
          {(lineItem.couponDetail!.length > 0 || Array.isArray(lineItem.loyaltyOffers)) &&
            this.renderRemoveLineItemBoundCouponModal()}

          {['PIN1', 'PRO1'].includes(model.runMode) &&
            !lineItem.issuedToRO &&
            lineItem.detail.serialNumberRequired &&
            this.renderSerialNumbersSelectionModal()}

          {/* net/list/trade in the data key below could be previously forced values */}
          {this.showForcePartModal && (
            <ForcePartModal
              applyForcedPricing={(netPriceForce: number, listPriceForce: number, tradePriceForce: number) => {
                model.applyForcedPricing(
                  itemIndex,
                  netPriceForce,
                  listPriceForce,
                  tradePriceForce,
                  this.lastManagerOverridePPC
                );
                model.updateTaxes();
                this.showForcePartModal = false;
              }}
              clearForcedPricing={() => {
                if (lineItem.forcedPricingFromQuoteOrConfirmation) {
                  model.clearForcedPricing(itemIndex, true);
                } else {
                  model.clearForcedPricing(itemIndex);
                }
                model.updateTaxes();
                this.showForcePartModal = false;
              }}
              container={getContainer}
              data={{
                escalatorListPrice: model.sysParams.escalatorListPrice,
                escalatorTradePrice: model.sysParams.escalatorTradePrice,
                listPrice,
                netPrice: lineItem.forcedPricing ? netPrice : publishedNetPrice,
                partNum: lineItem.partNum,
                tradePrice,
              }}
              forceExists={lineItem.forcedPricing!}
              hide={() => (this.showForcePartModal = false)}
              show={this.showForcePartModal}
              showGrossProfitPercentage={model.sysParams.displayGrossProfitOnInquiry}
              showNetPrice={model.sysParams.displayNetOnPartsInquiry}
            />
          )}
        </div>
      );

      // Is this a default part?  If so, we use a special default part modal for price config / overrides.
    } else if (lineItem.defaultPartNum) {
      const unpriced = lineItem.unpriced;
      const quantity = this.safeToFixed(lineItem.quantity, 0);
      const listPrice = this.safeToFixed(lineItem.defaultPartListPrice!, 2);
      const netPrice = this.safeToFixed(lineItem.defaultPartNetPrice!, 2);
      const tradePrice = this.safeToFixed(lineItem.defaultPartTradePrice!, 2);

      // Gross profit % calcs
      let grossProfitPercentage = unpriced
        ? '-'
        : !model.sysParams.displayGrossProfitOnInquiry
        ? ''
        : tradePrice === '0.00'
        ? '0'
        : Math.trunc(
            ((lineItem.defaultPartTradePrice! - lineItem.defaultPartNetPrice!) / lineItem.defaultPartTradePrice!) * 100
          ).toFixed(0);

      // "displayNetOnPartsInquiry" setting ONLY applies when the system is set to show NET pricing (not T2/T3).
      let netPriceDisplay = '';
      switch (model.sysParams.partsInquiryDisplayOptions) {
        case 'N':
          netPriceDisplay = model.sysParams.displayNetOnPartsInquiry ? netPrice : '';
          break;
        case 'T2':
        case 'T3':
          netPriceDisplay = netPrice;
          break;
        default:
          // 'W', 'X', or nothing
          break;
      }

      if (model.hideAdditionalDetail) {
        grossProfitPercentage = '';
        netPriceDisplay = '';
      }

      renderedComponent = (
        <div
          className={classNames({
            'sk-lineItemColsHoverable': !isDragging,
            'sk-lineItemIssuedToRO': lineItem.issuedToRO,
            'sk-lineItemSelectedForMoveOperation': lineItemSelectedForMoveOperation,
          })}
        >
          <table className="sk-lineItemCols">
            <tbody>
              <tr>
                {this.renderEditableQuantityCell(quantity)}
                {connectDragSource(
                  <td className={dragHandleClass}>
                    <MenuProvider event={contextMenuEvent} id={this.props.itemIndex.toString()}>
                      <span
                        title={lineItem.defaultPartNum}
                        onDoubleClick={() => this.copyToClipboard(lineItem.defaultPartNum!)}
                      >
                        {lineItem.issuedToRO ? this.renderOpLine() : ''}
                        {lineItem.defaultPartNum}
                      </span>
                    </MenuProvider>
                    <Menu id={this.props.itemIndex.toString()} animation="fade">
                      <Item
                        onClick={() => {
                          modal.open('partHistory', { lookupKey: lineItem.defaultPartNum! });
                        }}
                      >
                        Default Part History
                      </Item>
                    </Menu>
                  </td>
                )}
                <td />
                {this.renderDefaultPartDetailCell(lineItem.defaultPartDescription!)}
                <td>
                  {lineItem.issuedToRO && lineItem.roDetail!.subletPONum && (
                    <span
                      className="sk-hyperLink"
                      onClick={() => {
                        modal.open('purchaseOrderDetail', { lookupKey: lineItem.roDetail!.subletPONum });
                      }}
                    >
                      PO: {lineItem.roDetail!.subletPONum}
                    </span>
                  )}
                </td>
                <td>{lineItem.stockClass}</td>
                <td>{lineItem.priceSymbol}</td>
                {this.renderDefaultPartDetailCell(unpriced ? '-' : netPriceDisplay)}
                {this.renderDefaultPartDetailCell(unpriced ? '-' : listPrice)}
                {this.renderDefaultPartDetailCell(unpriced ? '-' : tradePrice)}
                <td>{grossProfitPercentage}</td>
                <td>
                  <span
                    className="sk-removeItemIcon"
                    onClick={() => (this.showConfirmRemoveModal = true)}
                    title="Remove Line Item"
                  >
                    <i className="zmdi zmdi-delete zmdi-hc-lg" />
                  </span>
                </td>
              </tr>

              {!model.hideAdditionalDetail &&
                this.renderDefaultPartSupplementalDetails(lineItem.defaultPartSupplementalDetails)}

              {/* Attach a "docked" comment to this line item which will move around with drags on sublet labor/parts */}
              {_.includes(['SUBL', 'SUBP'], lineItem.defaultPartNum) && (
                <tr>
                  <td />
                  <td />
                  <td />
                  <td className="sk-subletComment" colSpan={9}>
                    <span
                      className="sk-editField"
                      onClick={() => (this.showDefaultPartModal = true)}
                      title={lineItem.defaultPartSubletComment}
                    >
                      {lineItem.defaultPartSubletComment}
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {this.renderRemoveLineItemModal()}

          {/* For editing an existing default part entry.  This usage differs from the original default part entry from the part entry control. */}
          {this.showDefaultPartModal && (
            <DefaultPartModal
              apply={(
                netPriceUpdate: number,
                listPriceUpdate: number,
                tradePriceUpdate: number,
                descriptionUpdate: string,
                subletCommentUpdate: string,
                unpriced: boolean
              ) => {
                model.applyUpdateDefaultPart(
                  itemIndex,
                  netPriceUpdate,
                  listPriceUpdate,
                  tradePriceUpdate,
                  descriptionUpdate,
                  subletCommentUpdate,
                  unpriced
                );

                model.updateTaxes();
                this.showDefaultPartModal = false;
              }}
              container={getContainer}
              hide={() => (this.showDefaultPartModal = false)}
              data={{
                defaultPartNum: lineItem.defaultPartNum,
                description: lineItem.defaultPartDescription!,
                listPrice: unpriced ? '' : listPrice,
                netPrice: unpriced ? '' : netPrice,
                subletComment: lineItem.defaultPartSubletComment!,
                tradePrice: unpriced ? '' : tradePrice,
              }}
              model={model}
              show={this.showDefaultPartModal}
              unpricedOptionAvailable={
                ['PRO1', 'SMM3'].includes(this.model.runMode) &&
                (!lineItem.issuedToRO || lineItem.unpriced) &&
                !model.isEstimate
              }
              unpricedOptionChecked={unpriced}
            />
          )}
        </div>
      );
    } else if (lineItem.confirmationFreight || lineItem.confirmationFuel || lineItem.poFreight) {
      /* This is a special case wherein PO freight is being sold off of a confirmation.
         Typically, freight is added as a "default part" line item, but in this case under
         the covers, this is stored as a non-stocking part within the lineItem data structure
         (and QM expects us to replay it as a non-stocking part). We want it to behave
         as if it is a default part (FRT) within the invoice builder (for edits). We don't
         want things like the price symbol or stock class to be overwritten, which is
         the case when displaying non-stocking line items.  The implementation detail
         will appear therefore as a hybrid of the non-stocking / default part cases.
      */

      const listPrice = this.safeToFixed(lineItem.nonStockingPartListPrice!, 2);
      const netPrice = this.safeToFixed(lineItem.nonStockingPartNetPrice!, 2);
      const tradePrice = this.safeToFixed(lineItem.nonStockingPartTradePrice!, 2);

      // Gross profit % calcs
      let grossProfitPercentage = !model.sysParams.displayGrossProfitOnInquiry
        ? ''
        : tradePrice === '0.00'
        ? '0'
        : Math.trunc(
            ((lineItem.nonStockingPartTradePrice! - lineItem.nonStockingPartNetPrice!) /
              lineItem.nonStockingPartTradePrice!) *
              100
          ).toFixed(0);

      // "displayNetOnPartsInquiry" setting ONLY applies when the system is set to show NET pricing (not T2/T3).
      let netPriceDisplay = '';
      switch (model.sysParams.partsInquiryDisplayOptions) {
        case 'N':
          netPriceDisplay = model.sysParams.displayNetOnPartsInquiry ? netPrice : '';
          break;
        case 'T2':
        case 'T3':
          netPriceDisplay = netPrice;
          break;
        default:
          // 'W', 'X', or nothing
          break;
      }

      if (model.hideAdditionalDetail) {
        grossProfitPercentage = '';
        netPriceDisplay = '';
      }

      let defaultPartNum = '';
      let partDisplay = '';
      let partTitle = '';

      if (lineItem.confirmationFreight) {
        defaultPartNum = 'CONF-FREIGHT';
        partDisplay = `CONF-FREIGHT ${lineItem.confirmationSale!.confirmationNum}`;
        partTitle = `CONFIRMATION FREIGHT: CONFIRMATION ${lineItem.confirmationSale!.confirmationNum}`;
      } else if (lineItem.confirmationFuel) {
        defaultPartNum = 'CONF-FUEL';
        partDisplay = `CONF-FUEL ${lineItem.confirmationSale!.confirmationNum}`;
        partTitle = `CONFIRMATION FUEL: CONFIRMATION ${lineItem.confirmationSale!.confirmationNum}`;
      } else if (lineItem.poFreight) {
        defaultPartNum = 'PO-FREIGHT';
        partDisplay = `PO-FREIGHT ${lineItem.confirmationSale!.poNum}`;
        partTitle = `PO FREIGHT: CONFIRMATION ${lineItem.confirmationSale!.confirmationNum} PO ${
          lineItem.confirmationSale!.poNum
        }`;
      }

      renderedComponent = (
        <div
          className={classNames({
            'sk-lineItemColsHoverable': !isDragging,
            'sk-lineItemConfirmationSale': true,
            'sk-lineItemSelectedForMoveOperation': lineItemSelectedForMoveOperation,
          })}
        >
          <table className="sk-lineItemCols">
            <tbody>
              <tr>
                <td>
                  <span title={`${lineItem.quantity}`}>{lineItem.quantity}</span>
                </td>
                {connectDragSource(
                  <td className={dragHandleClass}>
                    <span title={partTitle} onDoubleClick={() => this.copyToClipboard(partTitle)}>
                      {partDisplay}
                    </span>
                  </td>
                )}
                <td />
                {this.renderDefaultPartDetailCell(lineItem.nonStockingPartDescription!)}
                <td />
                <td>{lineItem.stockClass}</td>
                <td>{lineItem.priceSymbol}</td>
                {this.renderDefaultPartDetailCell(netPriceDisplay)}
                {this.renderDefaultPartDetailCell(listPrice)}
                {this.renderDefaultPartDetailCell(tradePrice)}
                <td>{grossProfitPercentage}</td>
                <td>
                  <span
                    className="sk-removeItemIcon"
                    onClick={() => (this.showConfirmRemoveModal = true)}
                    title="Remove Line Item"
                  >
                    <i className="zmdi zmdi-delete zmdi-hc-lg" />
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          {this.renderRemoveLineItemModal()}

          {/* This is a "faux" default part display since it's represented as a non-stocking part in the line item data structure. */}
          {/* Therefore, the apply() callback is a little hacky and references applyUpdateNonStockingPart() */}
          {this.showDefaultPartModal && (
            <DefaultPartModal
              apply={(
                netPriceUpdate: number,
                listPriceUpdate: number,
                tradePriceUpdate: number,
                descriptionUpdate: string
              ) => {
                // Again, under the covers PO freight is stored as a non-stocking part for everything to work right with QM.
                model.applyUpdateNonStockingPart(
                  itemIndex,
                  lineItem.nonStockingPartNum!, // will not change
                  netPriceUpdate,
                  listPriceUpdate,
                  tradePriceUpdate,
                  descriptionUpdate,
                  lineItem.stockClass!, // will not change
                  lineItem.priceSymbol! // will not change
                );

                model.updateTaxes();
                this.showDefaultPartModal = false;
              }}
              container={getContainer}
              hide={() => (this.showDefaultPartModal = false)}
              data={{
                defaultPartNum,
                description: lineItem.nonStockingPartDescription!,
                listPrice,
                netPrice,
                subletComment: '',
                tradePrice,
              }}
              model={model}
              show={this.showDefaultPartModal}
            />
          )}
        </div>
      );
    } else if (lineItem.nonStockingPartNum) {
      const unpriced = lineItem.unpriced;
      const quantity = this.safeToFixed(lineItem.quantity, 0);
      const listPrice = this.safeToFixed(lineItem.nonStockingPartListPrice!, 2);
      const netPrice = this.safeToFixed(lineItem.nonStockingPartNetPrice!, 2);
      const tradePrice = this.safeToFixed(lineItem.nonStockingPartTradePrice!, 2);

      // Gross profit % calcs
      let grossProfitPercentage = unpriced
        ? '-'
        : !model.sysParams.displayGrossProfitOnInquiry
        ? ''
        : tradePrice === '0.00'
        ? '0'
        : Math.trunc(
            ((lineItem.nonStockingPartTradePrice! - lineItem.nonStockingPartNetPrice!) /
              lineItem.nonStockingPartTradePrice!) *
              100
          ).toFixed(0);

      // "displayNetOnPartsInquiry" setting ONLY applies when the system is set to show NET pricing (not T2/T3).
      let netPriceDisplay = '';
      switch (model.sysParams.partsInquiryDisplayOptions) {
        case 'N':
          netPriceDisplay = model.sysParams.displayNetOnPartsInquiry ? netPrice : '';
          break;
        case 'T2':
        case 'T3':
          netPriceDisplay = netPrice;
          break;
        default:
          // 'W', 'X', or nothing
          break;
      }

      if (model.hideAdditionalDetail) {
        grossProfitPercentage = '';
        netPriceDisplay = '';
      }

      const partTitle = `NON-TRACKED PART: @${lineItem.nonStockingPartNum}${
        lineItem.confirmationSale
          ? ` CONFIRMATION: ${lineItem.confirmationSale.confirmationNum} ${lineItem.confirmationSale.poNum}`
          : ''
      }`;

      renderedComponent = (
        <div
          className={classNames({
            'sk-lineItemColsHoverable': !isDragging,
            'sk-lineItemNonStocking': !lineItem.confirmationSale && !lineItem.issuedToRO,
            'sk-lineItemConfirmationSale': lineItem.confirmationSale && !lineItem.issuedToRO,
            'sk-lineItemIssuedToRO': lineItem.issuedToRO,
            'sk-lineItemSelectedForMoveOperation': lineItemSelectedForMoveOperation,
          })}
        >
          <table className="sk-lineItemCols">
            <tbody>
              <tr>
                {this.renderEditableQuantityCell(quantity)}
                {connectDragSource(
                  <td className={dragHandleClass}>
                    <MenuProvider event={contextMenuEvent} id={this.props.itemIndex.toString()}>
                      <span title={partTitle} onDoubleClick={() => this.copyToClipboard(lineItem.nonStockingPartNum!)}>
                        {lineItem.issuedToRO ? this.renderOpLine() : ''}@{lineItem.nonStockingPartNum}
                      </span>
                    </MenuProvider>
                    <PartContextMenu
                      id={this.props.itemIndex.toString()}
                      lookupKey={lineItem.nonStockingPartNum}
                      nonStocking={true}
                    />
                  </td>
                )}
                <td />
                {this.renderNonStockingPartDetailCell(lineItem.nonStockingPartDescription!)}
                <td />
                {this.renderNonStockingPartDetailCell(lineItem.stockClass!)}
                {this.renderNonStockingPartDetailCell(lineItem.priceSymbol!)}
                {this.renderNonStockingPartDetailCell(unpriced ? '-' : netPriceDisplay)}
                {this.renderNonStockingPartDetailCell(unpriced ? '-' : listPrice)}
                {this.renderNonStockingPartDetailCell(unpriced ? '-' : tradePrice)}
                <td>{grossProfitPercentage}</td>
                <td>
                  {!this.isSMM3 && (
                    <span
                      className="sk-removeItemIcon"
                      onClick={() => (this.showConfirmRemoveModal = true)}
                      title="Remove Line Item"
                    >
                      <i className="zmdi zmdi-delete zmdi-hc-lg" />
                    </span>
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          {this.renderRemoveLineItemModal()}

          {/* For editing an existing non stocking part entry.  This usage differs from the original non stocking part entry from the part entry control. */}
          {this.showNonStockingPartModal && (
            <NonStockingPartModal
              apply={(
                partNumUpdate: string,
                netPriceUpdate: number,
                listPriceUpdate: number,
                tradePriceUpdate: number,
                descriptionUpdate: string,
                stockClassUpdate: string,
                priceSymbolUpdate: string,
                unpriced: boolean
              ) => {
                model.applyUpdateNonStockingPart(
                  itemIndex,
                  partNumUpdate,
                  netPriceUpdate,
                  listPriceUpdate,
                  tradePriceUpdate,
                  descriptionUpdate,
                  stockClassUpdate,
                  priceSymbolUpdate,
                  unpriced
                );

                model.updateTaxes();
                this.showNonStockingPartModal = false;
              }}
              container={getContainer}
              hide={() => (this.showNonStockingPartModal = false)}
              data={{
                description: lineItem.nonStockingPartDescription!,
                listPrice: unpriced ? '' : listPrice,
                netPrice: unpriced ? '' : netPrice,
                partNum: lineItem.nonStockingPartNum,
                priceSymbol: lineItem.priceSymbol!,
                stockClass: lineItem.stockClass!,
                tradePrice: unpriced ? '' : tradePrice,
              }}
              model={model}
              show={this.showNonStockingPartModal}
              unpricedOptionAvailable={
                ['PRO1', 'SMM3'].includes(this.model.runMode) &&
                (!lineItem.issuedToRO || lineItem.unpriced) &&
                !model.isEstimate
              }
              unpricedOptionChecked={unpriced}
            />
          )}
        </div>
      );

      // Do we have recoverable errors with alternatives suggested? (subs, branches, divisions)
    } else if (lineItem.incompleteDetailErrors) {
      const quantity = this.safeToFixed(lineItem.quantity, 0);
      const expandIcon = `zmdi zmdi-hc-lg ${
        model.expandAllAlternatives || lineItem.showPartAlternatives ? 'zmdi-caret-up-circle' : 'zmdi-caret-down-circle'
      }`;

      // Logically, we only show bad parts in the invoice builder (as opposted to an error toastr) if some sort of
      // alternative part option is present, so partAlternativesExist should always be true here, this double checks.
      const detailSupplemental = lineItem.detailSupplemental;
      const partAlternativesExist =
        detailSupplemental &&
        (detailSupplemental.alternateDivisions.length !== 0 ||
          detailSupplemental.branchInventory.length !== 0 ||
          detailSupplemental.substitutions.length !== 0);

      renderedComponent = (
        <div
          className={classNames({
            'sk-lineItemColsHoverable': !isDragging,
            'sk-lineItemIncomplete': true,
          })}
        >
          <table className="sk-lineItemCols">
            <tbody>
              <tr className="sk-lineItemIncompleteDetail">
                <td>
                  <span title={quantity}>{quantity}</span>
                </td>
                {connectDragSource(
                  <td className={dragHandleClass} onContextMenu={evt => evt.preventDefault()}>
                    {partAlternativesExist && !model.hideAdditionalDetail && (
                      <span
                        className="sk-showPartAlternatives"
                        title="Show More Part Options"
                        onClick={() => {
                          if (partAlternativesExist && !model.expandAllAlternatives && !model.hideAdditionalDetail) {
                            lineItem.showPartAlternatives = !lineItem.showPartAlternatives;
                          }
                        }}
                      >
                        <i className={expandIcon} />
                      </span>
                    )}
                    <span title={lineItem.partNum} onDoubleClick={() => this.copyToClipboard(lineItem.partNum)}>
                      {lineItem.partNum}
                    </span>
                  </td>
                )}
                <td />
                <td>
                  {lineItem.incompleteDetailErrors.map((error, index) => {
                    return <div key={index}>{this.hyperError(error)}</div>;
                  })}
                </td>
                <td>
                  <span
                    className="sk-removeItemIcon"
                    onClick={() => model.removeLineItem(itemIndex)}
                    title="Remove Line Item"
                  >
                    <i className="zmdi zmdi-delete zmdi-hc-lg" />
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          {partAlternativesExist && this.renderCollapsableAlternatives({ detailSupplemental: detailSupplemental! })}
        </div>
      );

      // Should not be possible for this to occur in the model - failsafe / alert programming of issue
      // 6/10/22 this was useful for detecting empty memo lines due to a "MACHINE" sublet issue
    } else {
      renderedComponent = (
        <div className="sk-unrecoverableError">
          {lineItem.quantity}.{lineItem.partNum} - UNRECOVERABLE ERROR
        </div>
      );

      if (model.hideAdditionalDetail) {
        return null;
      }
    }

    return connectDragPreview(connectDropTarget(renderedComponent));
  }
}

function sourceProps(connect: any, monitor: any) {
  return {
    connectDragPreview: connect.dragPreview(),
    connectDragSource: connect.dragSource(),
    isDragging: monitor.isDragging(),
  };
}

function targetProps(connect: any) {
  return {
    connectDropTarget: connect.dropTarget(),
  };
}

export default DropTarget('LINEITEM', target, targetProps)(DragSource('LINEITEM', source, sourceProps)(LineItem));
